import React, { useState, useEffect } from "react";
import "./App.css";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Utilisateurs prédéfinis avec rôles
const USERS = [
  { username: "admin", password: "admin123", name: "Administrateur", role: "Admin" },
  { username: "comptable", password: "comptable123", name: "Comptable", role: "Comptable" },
  { username: "logistique", password: "logistique123", name: "Responsable Logistique", role: "Logistique" }
];

// Composant Login
const LoginForm = ({ onLogin }) => {
  const [credentials, setCredentials] = useState({ username: "", password: "" });
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");

    // Simuler un délai de connexion
    setTimeout(() => {
      const user = USERS.find(
        u => u.username === credentials.username && u.password === credentials.password
      );

      if (user) {
        localStorage.setItem("currentUser", JSON.stringify({
          username: user.username,
          name: user.name,
          role: user.role,
          loginTime: new Date().toISOString()
        }));
        onLogin(user);
      } else {
        setError("Nom d'utilisateur ou mot de passe incorrect");
      }
      setIsLoading(false);
    }, 800);
  };

  const handleChange = (field) => (e) => {
    setCredentials({ ...credentials, [field]: e.target.value });
    setError("");
  };

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        {/* Logo et titre */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-green-600 to-emerald-600 rounded-full mb-4">
            <svg className="w-10 h-10 text-white" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
            </svg>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">ARCHYSTORE</h1>
          <p className="text-gray-600">Gestion de Budget Construction</p>
        </div>

        {/* Formulaire de connexion */}
        <div className="bg-white rounded-lg shadow-lg p-8">
          <h2 className="text-2xl font-semibold text-gray-900 mb-6 text-center">Connexion</h2>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-2">
                Nom d'utilisateur
              </label>
              <input
                id="username"
                type="text"
                required
                value={credentials.username}
                onChange={handleChange("username")}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Entrez votre nom d'utilisateur"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                Mot de passe
              </label>
              <input
                id="password"
                type="password"
                required
                value={credentials.password}
                onChange={handleChange("password")}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Entrez votre mot de passe"
              />
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-md p-3">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-gradient-to-r from-green-600 to-emerald-600 text-white py-2 px-4 rounded-md hover:from-green-700 hover:to-emerald-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-all disabled:opacity-50"
            >
              {isLoading ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Connexion...
                </div>
              ) : (
                "Se connecter"
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

// ARCHYSTORE Brand Colors
const brandColors = {
  primary: '#4A90E2',    // Bleu
  secondary: '#FF6B35',  // Orange
  accent: '#E74C3C',     // Rouge
  dark: '#2C3E50',       // Gris foncé
  light: '#ECF0F1',      // Gris clair
  success: '#27AE60',    // Vert
  warning: '#F39C12',    // Orange foncé
};

// Format number with spaces as thousands separator
const formatAmount = (amount) => {
  return new Intl.NumberFormat('fr-FR', {
    style: 'decimal',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount).replace(/,/g, ' ');
};

// Format input with spaces as user types
const formatNumberInput = (value) => {
  const numericValue = value.replace(/\D/g, '');
  return numericValue.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
};

// Parse formatted number back to numeric value
const parseFormattedNumber = (value) => {
  return parseInt(value.replace(/\s/g, ''), 10) || 0;
};

const EditableCell = ({ value, onSave, type = "text", disabled = false }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);

  // Update editValue when value prop changes
  useEffect(() => {
    setEditValue(value);
  }, [value]);

  const handleSave = () => {
    if (!disabled && editValue !== value) {
      onSave(editValue);
    }
    setIsEditing(false);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSave();
    }
    if (e.key === 'Escape') {
      setEditValue(value);
      setIsEditing(false);
    }
  };

  // Common styles for both input and div
  const commonStyles = {
    width: '100%',
    minHeight: '24px',
    padding: '2px 6px',
    fontSize: '11px',
    lineHeight: '1.2',
    margin: '0',
    border: 'none',
    outline: 'none',
    backgroundColor: 'transparent',
    boxSizing: 'border-box',
    wordWrap: 'break-word',
    overflowWrap: 'break-word'
  };

  if (disabled) {
    return (
      <div 
        style={{
          ...commonStyles,
          backgroundColor: '#f3f4f6',
          color: '#6b7280',
          display: 'flex',
          alignItems: 'center'
        }}
      >
        {value}
      </div>
    );
  }

  if (isEditing) {
    return (
      <textarea
        value={editValue}
        onChange={(e) => setEditValue(e.target.value)}
        onBlur={handleSave}
        onKeyDown={handleKeyPress}
        style={{
          ...commonStyles,
          backgroundColor: '#ffffff',
          resize: 'none',
          height: 'auto',
          minHeight: '24px'
        }}
        autoFocus
      />
    );
  }

  return (
    <div
      onClick={() => setIsEditing(true)}
      style={{
        ...commonStyles,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        whiteSpace: 'normal'
      }}
      onMouseEnter={(e) => e.target.style.backgroundColor = '#f9fafb'}
      onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
    >
      {value}
    </div>
  );
};

// Format date to European format DD/MM/YYYY
const formatDateEuropean = (dateString) => {
  if (!dateString) return '';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  } catch (error) {
    console.error('Error formatting date:', error);
    return '';
  }
};

// Fonction pour télécharger le modèle Excel des dépenses
const downloadExpenseTemplate = () => {
  const csvContent = "data:text/csv;charset=utf-8,Date,Description,Quantité,Prix,Montant\n17/01/2024,Ciment Portland,100,35500,3550000\n18/01/2024,Sable de rivière,50,60000,3000000\n19/01/2024,Salaire équipe,1,2735000,2735000";
  const encodedUri = encodeURI(csvContent);
  const link = document.createElement("a");
  link.setAttribute("href", encodedUri);
  link.setAttribute("download", "modele_depenses.csv");
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

// Fonction pour télécharger le modèle Excel du prévisionnel
const downloadMaterialTemplate = () => {
  const csvContent = "data:text/csv;charset=utf-8,DESIGNATIONS,UNITE,QUANTITE,PU,MONTANT\nCIMENT,SAC,399,35500,14164500\nSABLE,M3,80,60000,4800000\nGRAVILLON,M3,35,90000,3150000";
  const encodedUri = encodeURI(csvContent);
  const link = document.createElement("a");
  link.setAttribute("href", encodedUri);
  link.setAttribute("download", "modele_previsionnel.csv");
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

// Fonction pour télécharger un fichier de manière sécurisée (compatible Chrome Windows)
const safeDownload = (blob, filename) => {
  try {
    // Méthode 1: Utiliser l'API moderne si disponible
    if (window.showSaveFilePicker) {
      const handle = window.showSaveFilePicker({
        suggestedName: filename,
        types: [{
          description: 'Files',
          accept: { '*/*': ['.pdf', '.xlsx'] }
        }]
      });
      handle.then(fileHandle => {
        const writable = fileHandle.createWritable();
        writable.write(blob);
        writable.close();
      }).catch(() => {
        // Fallback si l'utilisateur annule
        fallbackDownload(blob, filename);
      });
    } else {
      fallbackDownload(blob, filename);
    }
  } catch (error) {
    console.warn('Modern download failed, using fallback:', error);
    fallbackDownload(blob, filename);
  }
};

// Fonction de fallback pour le téléchargement
const fallbackDownload = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  
  // Utiliser un iframe caché pour éviter les problèmes DOM de Chrome
  const iframe = document.createElement('iframe');
  iframe.style.display = 'none';
  iframe.style.position = 'absolute';
  iframe.style.left = '-9999px';
  document.body.appendChild(iframe);
  
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.style.display = 'none';
  
  // Ajouter le lien à l'iframe au lieu du body principal
  iframe.contentDocument.body.appendChild(link);
  link.click();
  
  // Nettoyage avec plusieurs tentatives pour Chrome
  const cleanup = () => {
    try {
      if (iframe.contentDocument && iframe.contentDocument.body.contains(link)) {
        iframe.contentDocument.body.removeChild(link);
      }
    } catch (e) {
      console.warn('Link cleanup failed:', e);
    }
    
    try {
      if (document.body.contains(iframe)) {
        document.body.removeChild(iframe);
      }
    } catch (e) {
      console.warn('Iframe cleanup failed:', e);
    }
    
    try {
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.warn('URL cleanup failed:', e);
    }
  };
  
  // Nettoyage progressif pour Chrome
  setTimeout(cleanup, 100);
  setTimeout(cleanup, 500);
  setTimeout(cleanup, 1000);
};
// Fonction pour exporter en PDF
const exportToPDF = async (type, data, title) => {
  try {
    let url = '';
    let filename = '';
    
    if (type === 'project') {
      url = `${API}/projects/${data.id}/export-pdf`;
      filename = `rapport_${data.name.replace(/\s+/g, '_')}.pdf`;
    } else if (type === 'expenses') {
      url = `${API}/projects/${data.projectId}/tranches/${data.trancheId}/export-expenses-pdf`;
      filename = `depenses_${data.trancheName.replace(/\s+/g, '_')}.pdf`;
    }
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/pdf',
      },
    });
    
    if (!response.ok) {
      throw new Error('Export failed');
    }
    
    const blob = await response.blob();
    safeDownload(blob, filename);
    
  } catch (error) {
    console.error('Error exporting PDF:', error);
    alert('Erreur lors de l\'export PDF');
  }
};

// Fonction pour exporter les dépenses en Excel
const exportToExcel = async (expenses, trancheName) => {
  if (!expenses || expenses.length === 0) {
    alert('Aucune dépense à exporter');
    return;
  }

  try {
    // Préparer les données pour l'export Excel
    const data = expenses.map(expense => ({
      Date: formatDateEuropean(expense.date),
      Description: expense.description,
      'Quantité': expense.quantity,
      'Prix unitaire (Ar)': expense.unit_price,
      'Montant (Ar)': expense.amount
    }));

    // Ajouter le total
    const totalAmount = expenses.reduce((sum, expense) => sum + expense.amount, 0);
    data.push({
      Date: '',
      Description: '',
      'Quantité': '',
      'Prix unitaire (Ar)': 'TOTAL',
      'Montant (Ar)': totalAmount
    });

    // Créer le fichier Excel via le backend
    const response = await fetch(`${API}/export-excel`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        data: data,
        filename: `depenses_${trancheName.replace(/\s+/g, '_')}`
      })
    });

    if (!response.ok) {
      throw new Error('Export failed');
    }

    const blob = await response.blob();
    const filename = `depenses_${trancheName.replace(/\s+/g, '_')}.xlsx`;
    safeDownload(blob, filename);
    
  } catch (error) {
    console.error('Error exporting to Excel:', error);
    alert('Erreur lors de l\'export Excel');
  }
};

// Fonction pour supprimer toutes les dépenses d'une tranche
const deleteAllExpenses = async (projectId, trancheId) => {
  try {
    const response = await axios.delete(`${API}/projects/${projectId}/tranches/${trancheId}/expenses`);
    return response.data;
  } catch (error) {
    console.error('Error deleting all expenses:', error);
    throw error;
  }
};

// Fonction pour supprimer tous les matériaux d'une tranche
const deleteAllMaterials = async (projectId, trancheId) => {
  try {
    const response = await axios.delete(`${API}/projects/${projectId}/tranches/${trancheId}/materials`);
    return response.data;
  } catch (error) {
    console.error('Error deleting all materials:', error);
    throw error;
  }
};

// Fonction pour vérifier les doublons
const checkDuplicates = async (projectId, trancheId) => {
  try {
    const response = await axios.get(`${API}/projects/${projectId}/tranches/${trancheId}/check-duplicates`);
    return response.data;
  } catch (error) {
    console.error('Error checking duplicates:', error);
    throw error;
  }
};

// Fonction pour obtenir les informations de la semaine
// Get week number from project start date
const getWeekInfo = (dateString, projectStartDate) => {
  if (!dateString || !projectStartDate) {
    return {
      weekNumber: 1,
      monday: '',
      sunday: '',
      label: 'Semaine 1'
    };
  }
  
  try {
    const date = new Date(dateString);
    const startDate = new Date(projectStartDate);
    
    if (isNaN(date.getTime()) || isNaN(startDate.getTime())) {
      return {
        weekNumber: 1,
        monday: '',
        sunday: '',
        label: 'Semaine 1'
      };
    }
    
    // Calculate days difference from project start
    const daysDiff = Math.floor((date - startDate) / (24 * 60 * 60 * 1000));
    const weekNumber = Math.max(1, Math.ceil((daysDiff + 1) / 7));
    
    // Get Monday of the week containing this date
    const monday = new Date(date);
    monday.setDate(date.getDate() - (date.getDay() || 7) + 1);
    
    // Get Sunday of the week containing this date
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    
    return {
      weekNumber,
      monday: formatDateEuropean(monday),
      sunday: formatDateEuropean(sunday),
      label: `Semaine ${weekNumber} (${formatDateEuropean(monday)} - ${formatDateEuropean(sunday)})`
    };
  } catch (error) {
    console.error('Error in getWeekInfo:', error);
    return {
      weekNumber: 1,
      monday: '',
      sunday: '',
      label: 'Semaine 1'
    };
  }
};

// ARCHYSTORE Logo Component
const ArchyStoreLogo = () => (
  <div className="flex items-center space-x-3">
    <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center shadow-lg border">
      <div className="w-8 h-8 bg-gray-600 rounded flex items-center justify-center">
        <span className="text-white font-bold text-lg">A</span>
      </div>
    </div>
    <div>
      <h1 className="text-2xl font-bold text-gray-800">
        ARCHYSTORE
      </h1>
      <p className="text-sm text-gray-600 font-semibold">Construction & Bâtiment</p>
    </div>
  </div>
);

// Icon components for actions
const EditIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
  </svg>
);

const DeleteIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
  </svg>
);

const PlusIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
  </svg>
);

const UploadIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
  </svg>
);

const ProjectForm = ({ project, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    name: project?.name || '',
    location: project?.location || '',
    client: project?.client || '',
    start_date: project?.start_date || '',
    total_budget: project?.total_budget ? formatAmount(project.total_budget) : ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({
      ...formData,
      total_budget: parseFormattedNumber(formData.total_budget)
    });
  };

  const handleBudgetChange = (e) => {
    const formatted = formatNumberInput(e.target.value);
    setFormData({...formData, total_budget: formatted});
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl p-8 w-96 max-w-md">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-800">
            {project ? 'Modifier Chantier' : 'Nouveau Chantier'}
          </h2>
          <div className="w-8 h-8 bg-gradient-to-br from-orange-500 to-red-500 rounded-lg flex items-center justify-center">
            <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 2L2 7l10 5 10-5-10-5z"/>
            </svg>
          </div>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Nom du chantier
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Localisation
            </label>
            <input
              type="text"
              value={formData.location}
              onChange={(e) => setFormData({...formData, location: e.target.value})}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Client
            </label>
            <input
              type="text"
              value={formData.client}
              onChange={(e) => setFormData({...formData, client: e.target.value})}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Date de début
            </label>
            <input
              type="date"
              value={formData.start_date}
              onChange={(e) => setFormData({...formData, start_date: e.target.value})}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Budget total (Ar)
            </label>
            <input
              type="text"
              value={formData.total_budget}
              onChange={handleBudgetChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              placeholder="ex: 50 000 000"
              required
            />
          </div>
          <div className="flex justify-end space-x-3 pt-6">
            <button
              type="button"
              onClick={onCancel}
              className="px-6 py-3 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Annuler
            </button>
            <button
              type="submit"
              className="px-6 py-3 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-lg hover:from-orange-600 hover:to-red-600 transition-all transform hover:scale-105 shadow-lg"
            >
              {project ? 'Modifier' : 'Créer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const TrancheForm = ({ tranche, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    name: tranche?.name || '',
    budget_planned: tranche?.budget_planned ? formatAmount(tranche.budget_planned) : '',
    is_paid: tranche?.is_paid || false
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({
      ...formData,
      budget_planned: parseFormattedNumber(formData.budget_planned)
    });
  };

  const handleBudgetChange = (e) => {
    const formatted = formatNumberInput(e.target.value);
    setFormData({...formData, budget_planned: formatted});
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl p-8 w-96 max-w-md">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-800">
            {tranche ? 'Modifier Tranche' : 'Nouvelle Tranche'}
          </h2>
          <div className="w-8 h-8 bg-gradient-to-br from-orange-500 to-red-500 rounded-lg flex items-center justify-center">
            <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 2L2 7l10 5 10-5-10-5z"/>
            </svg>
          </div>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Nom de la tranche
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              required
              placeholder="ex: Tranche 1"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Budget (Ar)
            </label>
            <input
              type="text"
              value={formData.budget_planned}
              onChange={handleBudgetChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              placeholder="ex: 15 000 000"
              required
            />
          </div>
          <div className="flex items-center space-x-3">
            <input
              type="checkbox"
              id="is_paid"
              checked={formData.is_paid}
              onChange={(e) => setFormData({...formData, is_paid: e.target.checked})}
              className="w-4 h-4 text-orange-500 border-gray-300 rounded focus:ring-orange-500"
            />
            <label htmlFor="is_paid" className="text-sm font-semibold text-gray-700">
              Tranche payée
            </label>
          </div>
          <div className="flex justify-end space-x-3 pt-6">
            <button
              type="button"
              onClick={onCancel}
              className="px-6 py-3 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Annuler
            </button>
            <button
              type="submit"
              className="px-6 py-3 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-lg hover:from-orange-600 hover:to-red-600 transition-all transform hover:scale-105 shadow-lg"
            >
              {tranche ? 'Modifier' : 'Créer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const ExpenseForm = ({ expense, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    date: expense?.date || '',
    description: expense?.description || '',
    quantity: expense?.quantity || '',
    unit_price: expense?.unit_price ? formatAmount(expense.unit_price) : '',
    amount: expense?.amount ? formatAmount(expense.amount) : ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({
      ...formData,
      quantity: parseFloat(formData.quantity),
      unit_price: parseFormattedNumber(formData.unit_price),
      amount: parseFormattedNumber(formData.amount)
    });
  };

  const handlePriceChange = (e) => {
    const formatted = formatNumberInput(e.target.value);
    setFormData({...formData, unit_price: formatted});
  };

  const handleAmountChange = (e) => {
    const formatted = formatNumberInput(e.target.value);
    setFormData({...formData, amount: formatted});
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-96 max-w-md">
        <h2 className="text-xl font-bold mb-4 text-gray-800">
          {expense ? 'Modifier Dépense' : 'Nouvelle Dépense'}
        </h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Date
            </label>
            <input
              type="date"
              value={formData.date}
              onChange={(e) => setFormData({...formData, date: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <input
              type="text"
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Quantité
              </label>
              <input
                type="number"
                step="0.01"
                value={formData.quantity}
                onChange={(e) => setFormData({...formData, quantity: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Prix unitaire (Ar)
              </label>
              <input
                type="text"
                value={formData.unit_price}
                onChange={handlePriceChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Montant total (Ar)
            </label>
            <input
              type="text"
              value={formData.amount}
              onChange={handleAmountChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>
          <div className="flex justify-end space-x-2 pt-4">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Annuler
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 relative z-10"
            >
              {expense ? 'Modifier' : 'Ajouter'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const MaterialForm = ({ material, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    designation: material?.designation || '',
    unit: material?.unit || '',
    quantity_planned: material?.quantity_planned || '',
    unit_price: material?.unit_price ? formatAmount(material.unit_price) : ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    const quantity = parseFloat(formData.quantity_planned);
    const unitPrice = parseFormattedNumber(formData.unit_price);
    const amount = quantity * unitPrice;
    
    onSubmit({
      ...formData,
      quantity_planned: quantity,
      quantity_actual: material?.quantity_actual || 0,
      unit_price: unitPrice,
      amount_planned: amount,
      amount_actual: material?.amount_actual || 0
    });
  };

  const handlePriceChange = (e) => {
    const formatted = formatNumberInput(e.target.value);
    setFormData({...formData, unit_price: formatted});
  };

  const calculatedAmount = parseFloat(formData.quantity_planned) * parseFormattedNumber(formData.unit_price);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-96 max-w-md">
        <h2 className="text-xl font-bold mb-4 text-gray-800">
          {material ? 'Modifier Matériau' : 'Nouveau Matériau'}
        </h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Désignation
            </label>
            <input
              type="text"
              value={formData.designation}
              onChange={(e) => setFormData({...formData, designation: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
              placeholder="ex: Ciment"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Unité
            </label>
            <input
              type="text"
              value={formData.unit}
              onChange={(e) => setFormData({...formData, unit: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
              placeholder="ex: SAC"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Quantité prévue
              </label>
              <input
                type="number"
                step="0.01"
                value={formData.quantity_planned}
                onChange={(e) => setFormData({...formData, quantity_planned: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Prix unitaire (Ar)
              </label>
              <input
                type="text"
                value={formData.unit_price}
                onChange={handlePriceChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Montant prévisionnel (Ar)
            </label>
            <div className="w-full px-3 py-2 border border-gray-200 rounded-md bg-gray-50 text-gray-700">
              {isNaN(calculatedAmount) ? '0' : formatAmount(calculatedAmount)} Ar
            </div>
          </div>
          <div className="flex justify-end space-x-2 pt-4">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Annuler
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              {material ? 'Modifier' : 'Ajouter'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const ProjectDetail = ({ project, onBack, onUpdate, onDelete, currentUser, canAccessTab }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [tranches, setTranches] = useState([]);
  const [showProjectForm, setShowProjectForm] = useState(false);
  const [showTrancheForm, setShowTrancheForm] = useState(false);
  const [showExpenseForm, setShowExpenseForm] = useState(false);
  const [showMaterialForm, setShowMaterialForm] = useState(false);
  const [selectedTrancheId, setSelectedTrancheId] = useState(null);
  const [selectedTranche, setSelectedTranche] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dataCache, setDataCache] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [editingItem, setEditingItem] = useState(null);
  const [fileInputRef, setFileInputRef] = useState(null);
  const [showDuplicatesModal, setShowDuplicatesModal] = useState(false);
  const [duplicatesData, setDuplicatesData] = useState(null);
  
  // États pour les commandes
  const [orders, setOrders] = useState([]);
  const [showOrderForm, setShowOrderForm] = useState(false);
  const [selectedMaterialForOrder, setSelectedMaterialForOrder] = useState(null);
  const [materialsSummary, setMaterialsSummary] = useState([]);

  useEffect(() => {
    fetchTranches();
  }, [project.id]);

  useEffect(() => {
    if (selectedTrancheId) {
      if (activeTab === 'expenses') {
        fetchExpenses(selectedTrancheId);
      } else if (activeTab === 'materials') {
        fetchMaterials(selectedTrancheId);
      }
    }
  }, [selectedTrancheId, activeTab]);

  // Auto-load expenses when switching to expenses tab with selected tranche
  useEffect(() => {
    if (activeTab === 'expenses' && selectedTrancheId) {
      fetchExpenses(selectedTrancheId);
    }
  }, [activeTab]);

  const fetchTranches = async () => {
    if (loading) return;
    setLoading(true);
    try {
      const response = await axios.get(`${API}/projects/${project.id}/tranches`);
      setTranches(response.data);
      setDataCache(prev => ({ ...prev, tranches: response.data }));
      
      // Auto-select the last paid tranche, or the first tranche if none are paid
      if (response.data.length > 0 && !selectedTrancheId) {
        const paidTranches = response.data.filter(t => t.is_paid);
        const defaultTranche = paidTranches.length > 0 ? paidTranches[paidTranches.length - 1] : response.data[0];
        setSelectedTrancheId(defaultTranche.id);
        setSelectedTranche(defaultTranche);
      }
    } catch (error) {
      console.error('Error fetching tranches:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchExpenses = async (trancheId) => {
    if (loading) return;
    const cacheKey = `expenses_${trancheId}`;
    if (dataCache[cacheKey]) {
      setExpenses(dataCache[cacheKey]);
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.get(`${API}/projects/${project.id}/tranches/${trancheId}/expenses`);
      setExpenses(response.data);
      setDataCache(prev => ({ ...prev, [cacheKey]: response.data }));
    } catch (error) {
      console.error('Error fetching expenses:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMaterials = async (trancheId) => {
    if (loading) return;
    const cacheKey = `materials_${trancheId}`;
    if (dataCache[cacheKey]) {
      setMaterials(dataCache[cacheKey]);
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.get(`${API}/projects/${project.id}/tranches/${trancheId}/materials`);
      setMaterials(response.data);
      setDataCache(prev => ({ ...prev, [cacheKey]: response.data }));
    } catch (error) {
      console.error('Error fetching materials:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateProject = async (projectData) => {
    try {
      await axios.put(`${API}/projects/${project.id}`, projectData);
      setShowProjectForm(false);
      onUpdate();
    } catch (error) {
      console.error('Error updating project:', error);
    }
  };

  const handleDeleteProject = async () => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce chantier ?')) {
      try {
        await axios.delete(`${API}/projects/${project.id}`);
        onDelete();
      } catch (error) {
        console.error('Error deleting project:', error);
      }
    }
  };

  const handleCreateTranche = async (trancheData) => {
    try {
      if (editingItem) {
        // Update existing tranche
        await axios.put(`${API}/projects/${project.id}/tranches/${editingItem.id}`, trancheData);
      } else {
        // Create new tranche
        await axios.post(`${API}/projects/${project.id}/tranches`, trancheData);
      }
      setShowTrancheForm(false);
      setEditingItem(null);
      // Invalidate cache
      setDataCache(prev => {
        const newCache = { ...prev };
        delete newCache.tranches;
        return newCache;
      });
      fetchTranches();
    } catch (error) {
      console.error('Error saving tranche:', error);
    }
  };

  const handleCreateExpense = async (expenseData) => {
    try {
      await axios.post(`${API}/projects/${project.id}/tranches/${selectedTrancheId}/expenses`, expenseData);
      setShowExpenseForm(false);
      setEditingItem(null);
      // Invalidate cache
      setDataCache(prev => {
        const newCache = { ...prev };
        delete newCache[`expenses_${selectedTrancheId}`];
        return newCache;
      });
      fetchExpenses(selectedTrancheId);
    } catch (error) {
      console.error('Error creating expense:', error);
    }
  };

  const handleCreateMaterial = async (materialData) => {
    try {
      await axios.post(`${API}/projects/${project.id}/tranches/${selectedTrancheId}/materials`, materialData);
      setShowMaterialForm(false);
      setEditingItem(null);
      // Invalidate cache
      setDataCache(prev => {
        const newCache = { ...prev };
        delete newCache[`materials_${selectedTrancheId}`];
        return newCache;
      });
      fetchMaterials(selectedTrancheId);
    } catch (error) {
      console.error('Error creating material:', error);
    }
  };

  const handleUpdateExpense = async (expenseId, field, value) => {
    try {
      let updateData = {};
      let newAmount = null;
      
      // Find the current expense to get other values for calculation
      const currentExpense = expenses.find(exp => exp.id === expenseId);
      if (!currentExpense) {
        alert('Dépense introuvable');
        return;
      }
      
      // Parse the new value
      const parsedValue = field === 'date' ? value : 
                         field === 'quantity' ? parseFloat(value) : 
                         field === 'unit_price' || field === 'amount' ? parseFormattedNumber(value) : value;
      
      updateData[field] = parsedValue;
      
      // Auto-calculate amount if quantity or unit_price is changed
      if (field === 'quantity' || field === 'unit_price') {
        const newQuantity = field === 'quantity' ? parsedValue : currentExpense.quantity;
        const newUnitPrice = field === 'unit_price' ? parsedValue : currentExpense.unit_price;
        newAmount = newQuantity * newUnitPrice;
        updateData.amount = newAmount;
      }
      
      // Update the expense in the backend
      await axios.put(`${API}/projects/${project.id}/tranches/${selectedTrancheId}/expenses/${expenseId}`, updateData);
      
      // Update local state immediately for real-time feedback
      setExpenses(prevExpenses => 
        prevExpenses.map(expense => 
          expense.id === expenseId 
            ? { 
                ...expense, 
                [field]: parsedValue,
                ...(newAmount !== null ? { amount: newAmount } : {})
              }
            : expense
        )
      );
      
      // Invalidate cache and refresh
      setDataCache(prev => {
        const newCache = { ...prev };
        delete newCache[`expenses_${selectedTrancheId}`];
        return newCache;
      });
      
      // Refresh tranches to update totals
      fetchTranches();
      
    } catch (error) {
      console.error('Error updating expense:', error);
      alert('Erreur lors de la mise à jour de la dépense');
    }
  };

  const handleUpdateMaterial = async (materialId, field, value) => {
    try {
      let updateData = {};
      let newAmountPlanned = null;
      
      // Find the current material to get other values for calculation
      const currentMaterial = materials.find(mat => mat.id === materialId);
      if (!currentMaterial) {
        alert('Matériau introuvable');
        return;
      }
      
      // Parse the new value
      const parsedValue = field === 'quantity_planned' || field === 'quantity_actual' ? parseFloat(value) : 
                         field === 'unit_price' || field === 'amount_planned' || field === 'amount_actual' ? parseFormattedNumber(value) : value;
      
      updateData[field] = parsedValue;
      
      // Auto-calculate amount_planned if quantity_planned or unit_price is changed
      if (field === 'quantity_planned' || field === 'unit_price') {
        const newQuantityPlanned = field === 'quantity_planned' ? parsedValue : currentMaterial.quantity_planned;
        const newUnitPrice = field === 'unit_price' ? parsedValue : currentMaterial.unit_price;
        newAmountPlanned = newQuantityPlanned * newUnitPrice;
        updateData.amount_planned = newAmountPlanned;
      }
      
      // Update the material in the backend
      await axios.put(`${API}/projects/${project.id}/tranches/${selectedTrancheId}/materials/${materialId}`, updateData);
      
      // Update local state immediately for real-time feedback
      setMaterials(prevMaterials => 
        prevMaterials.map(material => 
          material.id === materialId 
            ? { 
                ...material, 
                [field]: parsedValue,
                ...(newAmountPlanned !== null ? { amount_planned: newAmountPlanned } : {})
              }
            : material
        )
      );
      
      // Invalidate cache and refresh
      setDataCache(prev => {
        const newCache = { ...prev };
        delete newCache[`materials_${selectedTrancheId}`];
        return newCache;
      });
      
    } catch (error) {
      console.error('Error updating material:', error);
      alert('Erreur lors de la mise à jour du matériau');
    }
  };

  const handleImportExpenses = async (trancheId, file) => {
    try {
      // Vérifier si le fichier a déjà été importé
      const existingExpenses = await axios.get(`${API}/projects/${project.id}/tranches/${trancheId}/expenses`);
      
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/projects/${project.id}/tranches/${trancheId}/import-expenses`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      // Vérifier les doublons potentiels
      const newExpenses = await axios.get(`${API}/projects/${project.id}/tranches/${trancheId}/expenses`);
      const duplicateCount = newExpenses.data.length - existingExpenses.data.length;
      
      if (duplicateCount > 0) {
        const confirmMessage = `Import terminé ! ${duplicateCount} nouvelles dépenses ajoutées.\n\n⚠️ Attention: Si vous avez déjà importé ce fichier, cela peut créer des doublons.\n\nVoulez-vous continuer ?`;
        
        if (window.confirm(confirmMessage)) {
          // Invalidate cache
          setDataCache(prev => {
            const newCache = { ...prev };
            delete newCache[`expenses_${trancheId}`];
            delete newCache.tranches;
            return newCache;
          });
          await fetchExpenses(trancheId);
          await fetchTranches();
          alert(`✅ Import confirmé ! ${response.data.message}`);
        }
      } else {
        // Invalidate cache
        setDataCache(prev => {
          const newCache = { ...prev };
          delete newCache[`expenses_${trancheId}`];
          delete newCache.tranches;
          return newCache;
        });
        await fetchExpenses(trancheId);
        await fetchTranches();
        alert(`✅ Import réussi ! ${response.data.message}`);
      }
    } catch (error) {
      console.error('Error importing expenses:', error);
      const errorMessage = error.response?.data?.detail || 'Erreur lors de l\'import';
      alert(`❌ Erreur: ${errorMessage}`);
    }
  };

  const handleImportMaterials = async (trancheId, file) => {
    try {
      // Vérifier si le fichier a déjà été importé
      const existingMaterials = await axios.get(`${API}/projects/${project.id}/tranches/${trancheId}/materials`);
      
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/projects/${project.id}/tranches/${trancheId}/import-materials`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      // Vérifier les doublons potentiels
      const newMaterials = await axios.get(`${API}/projects/${project.id}/tranches/${trancheId}/materials`);
      const duplicateCount = newMaterials.data.length - existingMaterials.data.length;
      
      if (duplicateCount > 0) {
        const confirmMessage = `Import terminé ! ${duplicateCount} nouveaux matériaux ajoutés.\n\n⚠️ Attention: Si vous avez déjà importé ce fichier, cela peut créer des doublons.\n\nVoulez-vous continuer ?`;
        
        if (window.confirm(confirmMessage)) {
          // Invalidate cache
          setDataCache(prev => {
            const newCache = { ...prev };
            delete newCache[`materials_${trancheId}`];
            delete newCache.tranches;
            return newCache;
          });
          await fetchMaterials(trancheId);
          await fetchTranches();
          alert(`✅ Import confirmé ! ${response.data.message}`);
        }
      } else {
        // Invalidate cache
        setDataCache(prev => {
          const newCache = { ...prev };
          delete newCache[`materials_${trancheId}`];
          delete newCache.tranches;
          return newCache;
        });
        await fetchMaterials(trancheId);
        await fetchTranches();
        alert(`✅ Import réussi ! ${response.data.message}`);
      }
    } catch (error) {
      console.error('Error importing materials:', error);
      const errorMessage = error.response?.data?.detail || 'Erreur lors de l\'import';
      alert(`❌ Erreur: ${errorMessage}`);
    }
  };

  const calculateTrancheActual = (tranche) => {
    return tranche.expenses?.reduce((total, expense) => total + expense.amount, 0) || 0;
  };

  const calculateTrancheResult = (tranche) => {
    const actual = calculateTrancheActual(tranche);
    return tranche.budget_planned - actual;
  };

  const calculateTranchePercentage = (tranche) => {
    const actual = calculateTrancheActual(tranche);
    if (tranche.budget_planned === 0) return 0;
    return ((actual - tranche.budget_planned) / tranche.budget_planned) * 100;
  };

  const totalBudgetPlanned = tranches.filter(tranche => tranche.is_paid).reduce((total, tranche) => total + tranche.budget_planned, 0);
  const totalActual = tranches.filter(tranche => tranche.is_paid).reduce((total, tranche) => total + calculateTrancheActual(tranche), 0);
  const totalResult = totalBudgetPlanned - totalActual;
  const totalPercentage = totalBudgetPlanned > 0 ? ((totalActual - totalBudgetPlanned) / totalBudgetPlanned) * 100 : 0;

  // Fonctions pour les commandes
  const fetchOrders = async (trancheId = null) => {
    try {
      const endpoint = trancheId 
        ? `${API}/projects/${project.id}/tranches/${trancheId}/orders`
        : `${API}/projects/${project.id}/orders`;
      const response = await axios.get(endpoint);
      setOrders(response.data);
    } catch (error) {
      console.error('Error fetching orders:', error);
    }
  };

  const fetchMaterialsSummary = async (trancheId) => {
    try {
      const response = await axios.get(`${API}/projects/${project.id}/tranches/${trancheId}/materials-summary`);
      setMaterialsSummary(response.data);
    } catch (error) {
      console.error('Error fetching materials summary:', error);
    }
  };

  const handleCreateOrder = async (orderData) => {
    try {
      await axios.post(`${API}/projects/${project.id}/tranches/${selectedTrancheId}/orders`, orderData);
      await fetchOrders(selectedTrancheId);
      await fetchMaterialsSummary(selectedTrancheId);
      setShowOrderForm(false);
      setSelectedMaterialForOrder(null);
      alert('Commande créée avec succès !');
    } catch (error) {
      console.error('Error creating order:', error);
      alert('Erreur lors de la création de la commande');
    }
  };

  const handleUpdateOrderStatus = async (orderId, newStatus) => {
    try {
      await axios.put(`${API}/orders/${orderId}`, { status: newStatus });
      await fetchOrders(selectedTrancheId);
      alert('Statut de la commande mis à jour !');
    } catch (error) {
      console.error('Error updating order:', error);
      alert('Erreur lors de la mise à jour du statut');
    }
  };

  const handleDeleteOrder = async (orderId) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette commande ?')) {
      try {
        await axios.delete(`${API}/orders/${orderId}`);
        await fetchOrders(selectedTrancheId);
        await fetchMaterialsSummary(selectedTrancheId);
        alert('Commande supprimée avec succès !');
      } catch (error) {
        console.error('Error deleting order:', error);
        alert('Erreur lors de la suppression de la commande');
      }
    }
  };

  // Charger les commandes quand la tranche change
  useEffect(() => {
    if (selectedTrancheId && activeTab === 'orders') {
      fetchOrders(selectedTrancheId);
      fetchMaterialsSummary(selectedTrancheId);
    }
  }, [selectedTrancheId, activeTab]);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <button
                onClick={onBack}
                className="mr-4 p-2 text-gray-500 hover:text-gray-700"
              >
                ← Retour
              </button>
              <h1 className="text-xl font-semibold text-gray-900 truncate">{project.name}</h1>
            </div>
            <div className="flex items-center space-x-2 sm:space-x-4">
              <div className="text-sm text-gray-500 hidden sm:block">
                {project.client} • {project.location}
              </div>
              <button
                onClick={() => setShowProjectForm(true)}
                className="px-2 sm:px-3 py-1 text-sm text-blue-600 border border-blue-600 rounded hover:bg-blue-50"
              >
                Modifier
              </button>
              <button
                onClick={handleDeleteProject}
                className="px-2 sm:px-3 py-1 text-sm text-red-600 border border-red-600 rounded hover:bg-red-50"
              >
                Supprimer
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="bg-white rounded-lg shadow-sm">
          <div className="border-b border-gray-200 mobile-tabs">
            <nav className="flex space-x-8 px-6" aria-label="Tabs">
              {canAccessTab('overview') && (
                <button
                  onClick={() => setActiveTab('overview')}
                  className={`${
                    activeTab === 'overview'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                >
                  Vue d'ensemble
                </button>
              )}
              {canAccessTab('tranches') && (
                <button
                  onClick={() => setActiveTab('tranches')}
                  className={`${
                    activeTab === 'tranches'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                >
                  Tranches
                </button>
              )}
              {canAccessTab('expenses') && (
                <button
                  onClick={() => setActiveTab('expenses')}
                  className={`${
                    activeTab === 'expenses'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                >
                  Dépenses
                </button>
              )}
              {canAccessTab('materials') && (
                <button
                  onClick={() => setActiveTab('materials')}
                  className={`${
                    activeTab === 'materials'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                >
                  Prévisionnel
                </button>
              )}
              {canAccessTab('orders') && (
                <button
                  onClick={() => setActiveTab('orders')}
                  className={`${
                    activeTab === 'orders'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                >
                  Commandes
                </button>
              )}
              {canAccessTab('comparison') && (
                <button
                  onClick={() => setActiveTab('comparison')}
                  className={`${
                    activeTab === 'comparison'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                >
                  Comparaison
                </button>
              )}
              {canAccessTab('salaires') && (
                <button
                  onClick={() => setActiveTab('salaires')}
                  className={`${
                    activeTab === 'salaires'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                >
                  Salaires
                </button>
              )}
            </nav>
          </div>

          <div className="p-6">
            {activeTab === 'overview' && canAccessTab('overview') && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="bg-blue-50 p-6 rounded-lg">
                    <h3 className="text-lg font-medium text-blue-900 mb-2">Budget Total</h3>
                    <p className="text-2xl font-bold text-blue-600">
                      {formatAmount(project.total_budget)} Ar
                    </p>
                  </div>
                  <div className="bg-green-50 p-6 rounded-lg">
                    <h3 className="text-lg font-medium text-green-900 mb-2">Dépenses Réelles</h3>
                    <p className="text-2xl font-bold text-green-600">
                      {formatAmount(totalActual)} Ar
                    </p>
                  </div>
                  <div className={`p-6 rounded-lg ${totalResult >= 0 ? 'bg-green-50' : 'bg-red-50'}`}>
                    <h3 className={`text-lg font-medium mb-2 ${totalResult >= 0 ? 'text-green-900' : 'text-red-900'}`}>
                      Résultat
                    </h3>
                    <p className={`text-2xl font-bold ${totalResult >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {totalResult >= 0 ? '+' : ''}{formatAmount(totalResult)} Ar
                    </p>
                    <p className={`text-sm ${totalResult >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {totalPercentage >= 0 ? '+' : ''}{totalPercentage.toFixed(1)}%
                    </p>
                  </div>
                </div>

                <div className="bg-white border rounded-lg overflow-hidden">
                  <div className="px-6 py-4 border-b bg-gray-50">
                    <h3 className="text-lg font-medium text-gray-900">Résumé par Tranche</h3>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Tranche
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Budget
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Dépenses Réelles
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Résultat (Ar)
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Écart %
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {tranches.map((tranche) => {
                          const actual = calculateTrancheActual(tranche);
                          const result = calculateTrancheResult(tranche);
                          const percentage = calculateTranchePercentage(tranche);
                          
                          return (
                            <tr key={tranche.id}>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                {tranche.name}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {formatAmount(tranche.budget_planned)} Ar
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {formatAmount(actual)} Ar
                              </td>
                              <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium ${result >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {result >= 0 ? '+' : ''}{formatAmount(result)} Ar
                              </td>
                              <td className={`px-6 py-4 whitespace-nowrap text-sm ${percentage <= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {percentage >= 0 ? '+' : ''}{percentage.toFixed(1)}%
                              </td>
                            </tr>
                          );
                        })}
                        <tr className="bg-gray-50 font-medium">
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            Total
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {formatAmount(totalBudgetPlanned)} Ar
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {formatAmount(totalActual)} Ar
                          </td>
                          <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium ${totalResult >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {totalResult >= 0 ? '+' : ''}{formatAmount(totalResult)} Ar
                          </td>
                          <td className={`px-6 py-4 whitespace-nowrap text-sm ${totalPercentage <= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {totalPercentage >= 0 ? '+' : ''}{totalPercentage.toFixed(1)}%
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'tranches' && (
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-medium text-gray-900">Gestion des Tranches</h3>
                  <button
                    onClick={() => setShowTrancheForm(true)}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    Nouvelle Tranche
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {tranches.map((tranche) => {
                    const actual = calculateTrancheActual(tranche);
                    const result = calculateTrancheResult(tranche);
                    const percentage = calculateTranchePercentage(tranche);
                    
                    return (
                      <div key={tranche.id} className={`bg-white border rounded-lg p-6 hover:shadow-md transition-shadow ${
                        tranche.is_paid ? 'border-green-300 bg-green-50' : 'border-red-300 bg-red-50'
                      }`}>
                        <div className="flex items-center justify-between mb-4">
                          <h4 className="text-lg font-medium text-gray-900">{tranche.name}</h4>
                          <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                            tranche.is_paid 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {tranche.is_paid ? 'Payée' : 'Non payée'}
                          </span>
                        </div>
                        <div className="space-y-3">
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-500">Budget:</span>
                            <span className="text-sm font-medium">{formatAmount(tranche.budget_planned)} Ar</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-500">Dépenses réelles:</span>
                            <span className="text-sm font-medium">{formatAmount(actual)} Ar</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-500">Résultat:</span>
                            <span className={`text-sm font-medium ${result >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {result >= 0 ? '+' : ''}{formatAmount(result)} Ar
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-500">Écart:</span>
                            <span className={`text-sm font-medium ${percentage <= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {percentage >= 0 ? '+' : ''}{percentage.toFixed(1)}%
                            </span>
                          </div>
                        </div>
                        <div className="mt-4 pt-4 border-t">
                          <button
                            onClick={() => {
                              setEditingItem(tranche);
                              setShowTrancheForm(true);
                            }}
                            className="w-full px-3 py-2 text-sm text-blue-600 border border-blue-600 rounded-md hover:bg-blue-50 mb-2"
                          >
                            Modifier
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {activeTab === 'expenses' && (
              <div className="space-y-4">
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">Gestion des Dépenses</h3>
                    <div className="flex flex-col sm:flex-row sm:space-x-3 mobile-buttons">
                      <select
                        className="px-3 py-2 border border-orange-300 rounded-md text-sm focus:ring-2 focus:ring-orange-500 mb-2 sm:mb-0"
                        value={selectedTrancheId || ''}
                        onChange={(e) => {
                          const trancheId = e.target.value;
                          setSelectedTrancheId(trancheId);
                          if (trancheId) {
                            fetchExpenses(trancheId);
                            setSelectedTranche(tranches.find(t => t.id === trancheId));
                          } else {
                            setExpenses([]);
                            setSelectedTranche(null);
                          }
                        }}
                      >
                        <option value="">Sélectionner une tranche</option>
                        {tranches.map(tranche => (
                          <option key={tranche.id} value={tranche.id}>{tranche.name}</option>
                        ))}
                      </select>
                      {selectedTrancheId && (
                        <>
                          <button
                            onClick={() => setShowExpenseForm(true)}
                            className="inline-flex items-center px-3 py-2 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-md hover:from-orange-600 hover:to-red-600 transition-all text-sm"
                          >
                            <PlusIcon />
                            <span className="ml-1">Nouvelle</span>
                          </button>
                          <button
                            onClick={() => exportToExcel(expenses, selectedTranche?.name || 'Tranche')}
                            className="inline-flex items-center px-3 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 transition-all text-sm"
                          >
                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            Export
                          </button>
                          <div className="relative">
                            <input
                              type="file"
                              accept=".xlsx,.xls"
                              onChange={(e) => {
                                if (e.target.files[0]) {
                                  handleImportExpenses(selectedTrancheId, e.target.files[0]);
                                }
                              }}
                              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                            />
                            <button className="inline-flex items-center px-3 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 transition-all text-sm w-full sm:w-auto">
                              <UploadIcon />
                              <span className="ml-1">Import</span>
                            </button>
                          </div>
                          <button
                            onClick={() => exportToPDF('expenses', {
                              projectId: project.id,
                              trancheId: selectedTrancheId,
                              trancheName: selectedTranche?.name || 'Tranche'
                            }, 'Dépenses')}
                            className="inline-flex items-center px-3 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 transition-all text-sm"
                          >
                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            PDF
                          </button>
                          <button
                            onClick={async () => {
                              if (window.confirm('Êtes-vous sûr de vouloir supprimer toutes les dépenses de cette tranche ? Cette action est irréversible.')) {
                                try {
                                  await deleteAllExpenses(project.id, selectedTrancheId);
                                  setExpenses([]);
                                  alert('Toutes les dépenses ont été supprimées avec succès.');
                                } catch (error) {
                                  alert('Erreur lors de la suppression des dépenses.');
                                }
                              }
                            }}
                            className="inline-flex items-center px-3 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-all text-sm"
                          >
                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                            Supprimer tout
                          </button>
                          <button
                            onClick={async () => {
                              try {
                                const duplicates = await checkDuplicates(project.id, selectedTrancheId);
                                if (duplicates.has_duplicates) {
                                  setDuplicatesData({
                                    expenses: duplicates.duplicate_expenses,
                                    materials: duplicates.duplicate_materials,
                                    trancheName: selectedTranche?.name || 'Tranche'
                                  });
                                  setShowDuplicatesModal(true);
                                } else {
                                  alert('Aucun doublon détecté.');
                                }
                              } catch (error) {
                                alert('Erreur lors de la vérification des doublons.');
                              }
                            }}
                            className="inline-flex items-center px-3 py-2 bg-yellow-500 text-white rounded-md hover:bg-yellow-600 transition-all text-sm"
                          >
                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            Vérifier doublons
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                  
                  {selectedTrancheId && (
                    <div className="mb-4">
                      <div className="relative">
                        <input
                          type="text"
                          placeholder="Rechercher une dépense..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                        />
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                          </svg>
                        </div>
                        {searchTerm && (
                          <button
                            onClick={() => setSearchTerm('')}
                            className="absolute inset-y-0 right-0 pr-3 flex items-center"
                          >
                            <svg className="h-4 w-4 text-gray-400 hover:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        )}
                      </div>
                    </div>
                  )}

                {selectedTrancheId && (
                  <div className="bg-orange-50 border border-orange-200 p-4 rounded-lg">
                    <h4 className="font-semibold text-orange-900 mb-2">{selectedTranche?.name}</h4>
                    <p className="text-sm text-orange-700 mb-2">
                      Budget: {formatAmount(selectedTranche?.budget_planned || 0)} Ar
                    </p>
                    <div className="text-xs text-orange-600 bg-orange-100 p-2 rounded">
                      💡 <strong>Format Excel:</strong> Date, Description, Quantité, Prix, Montant
                    </div>
                  </div>
                )}

                {loading && (
                  <div className="fixed inset-0 bg-black bg-opacity-25 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 flex items-center space-x-4">
                      <div className="archystore-spinner"></div>
                      <span className="text-gray-700">Chargement...</span>
                    </div>
                  </div>
                )}

                {expenses.length > 0 && (
                  <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="min-w-full compact-table">
                        <thead className="bg-gradient-to-r from-orange-500 to-red-500 text-white">
                          <tr>
                            <th className="px-2 py-1 text-left text-xs font-semibold uppercase tracking-wider">Date</th>
                            <th className="px-2 py-1 text-left text-xs font-semibold uppercase tracking-wider">Description</th>
                            <th className="px-2 py-1 text-left text-xs font-semibold uppercase tracking-wider">Qté</th>
                            <th className="px-2 py-1 text-left text-xs font-semibold uppercase tracking-wider">Prix (Ar)</th>
                            <th className="px-2 py-1 text-left text-xs font-semibold uppercase tracking-wider">Montant (Ar)</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {/* Ligne de total en haut */}
                          <tr className="bg-orange-50 font-semibold text-sm border-b-2 border-orange-200">
                            <td colSpan="4" className="px-2 py-1 text-orange-900">TOTAL GÉNÉRAL</td>
                            <td className="px-2 py-1 text-orange-900 font-bold">
                              {formatAmount(expenses.reduce((sum, expense) => sum + expense.amount, 0))} Ar
                            </td>
                          </tr>
                          
                          {(() => {
                            // Filter expenses by search term
                            const filteredExpenses = expenses.filter(expense => {
                              if (!searchTerm) return true;
                              const searchLower = searchTerm.toLowerCase();
                              return expense.description.toLowerCase().includes(searchLower) ||
                                     expense.date.includes(searchTerm) ||
                                     expense.amount.toString().includes(searchTerm) ||
                                     expense.quantity.toString().includes(searchTerm);
                            });
                            
                            // Regrouper les dépenses filtrées par semaine
                            const expensesByWeek = filteredExpenses.reduce((groups, expense) => {
                              const weekInfo = getWeekInfo(expense.date, project.start_date);
                              if (!groups[weekInfo.weekNumber]) {
                                groups[weekInfo.weekNumber] = {
                                  weekInfo,
                                  expenses: [],
                                  total: 0
                                };
                              }
                              groups[weekInfo.weekNumber].expenses.push(expense);
                              groups[weekInfo.weekNumber].total += expense.amount;
                              return groups;
                            }, {});

                            // Show "no results" message if search returns no results
                            if (searchTerm && filteredExpenses.length === 0) {
                              return (
                                <tr>
                                  <td colSpan="5" className="px-2 py-4 text-center text-gray-500 text-sm">
                                    Aucune dépense trouvée pour "{searchTerm}"
                                  </td>
                                </tr>
                              );
                            }

                            return Object.keys(expensesByWeek)
                              .sort((a, b) => parseInt(a) - parseInt(b))
                              .map(weekNumber => {
                                const weekData = expensesByWeek[weekNumber];
                                
                                // Grouper les dépenses par date dans chaque semaine
                                const expensesByDate = weekData.expenses.reduce((dateGroups, expense) => {
                                  const date = expense.date;
                                  if (!dateGroups[date]) {
                                    dateGroups[date] = [];
                                  }
                                  dateGroups[date].push(expense);
                                  return dateGroups;
                                }, {});
                                
                                return (
                                  <React.Fragment key={`week-${weekNumber}`}>
                                    <tr className="bg-orange-100 font-semibold text-sm">
                                      <td colSpan="4" className="px-2 py-1 text-orange-800">
                                        {weekData.weekInfo.label}
                                      </td>
                                      <td className="px-2 py-1 text-orange-800 font-bold">
                                        {formatAmount(weekData.total)} Ar
                                      </td>
                                    </tr>
                                    {Object.keys(expensesByDate).sort().map(date => (
                                      <React.Fragment key={`date-${date}`}>
                                        {expensesByDate[date].map((expense, index) => (
                                          <tr key={`expense-${expense.id}`} className="hover:bg-orange-25">
                                            <td className="text-xs border-r border-gray-200">
                                              {index === 0 ? (
                                                <EditableCell
                                                  value={formatDateEuropean(expense.date)}
                                                  onSave={(value) => handleUpdateExpense(expense.id, 'date', value)}
                                                  type="date"
                                                />
                                              ) : (
                                                <div className="w-full h-full px-2 py-1 flex items-center">
                                                  <span className="text-gray-400">″</span>
                                                </div>
                                              )}
                                            </td>
                                            <td className="text-xs border-r border-gray-200">
                                              <EditableCell
                                                value={expense.description}
                                                onSave={(value) => handleUpdateExpense(expense.id, 'description', value)}
                                              />
                                            </td>
                                            <td className="text-xs border-r border-gray-200">
                                              <EditableCell
                                                value={expense.quantity}
                                                onSave={(value) => handleUpdateExpense(expense.id, 'quantity', value)}
                                                type="number"
                                              />
                                            </td>
                                            <td className="text-xs border-r border-gray-200">
                                              <EditableCell
                                                value={formatAmount(expense.unit_price)}
                                                onSave={(value) => handleUpdateExpense(expense.id, 'unit_price', value)}
                                              />
                                            </td>
                                            <td className="text-xs font-semibold">
                                              <EditableCell
                                                value={formatAmount(expense.amount)}
                                                onSave={(value) => handleUpdateExpense(expense.id, 'amount', value)}
                                                disabled={true}
                                              />
                                            </td>
                                          </tr>
                                        ))}
                                      </React.Fragment>
                                    ))}
                                  </React.Fragment>
                                );
                              });
                          })()}
                        </tbody>
                        <tfoot className="bg-orange-50 border-t-2 border-orange-200">
                          <tr className="font-semibold text-sm">
                            <td colSpan="4" className="px-2 py-1 text-orange-900">Budget de la tranche</td>
                            <td className="px-2 py-1 text-orange-900 font-bold">
                              {formatAmount(selectedTranche?.budget_planned || 0)} Ar
                            </td>
                          </tr>
                          <tr className="font-semibold text-sm">
                            <td colSpan="4" className="px-2 py-1">Différence</td>
                            <td className={`px-2 py-1 font-bold ${
                              (selectedTranche?.budget_planned || 0) - expenses.reduce((sum, expense) => sum + expense.amount, 0) >= 0 
                                ? 'text-green-600' 
                                : 'text-red-600'
                            }`}>
                              {(() => {
                                const diff = (selectedTranche?.budget_planned || 0) - expenses.reduce((sum, expense) => sum + expense.amount, 0);
                                return `${diff >= 0 ? '+' : ''}${formatAmount(diff)} Ar`;
                              })()}
                            </td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </div>
                )}

                {selectedTrancheId && expenses.length === 0 && (
                  <div className="text-center py-12">
                    <div className="text-gray-500 text-lg mb-4">Aucune dépense pour cette tranche</div>
                    <button
                      onClick={() => setShowExpenseForm(true)}
                      className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-lg hover:from-orange-600 hover:to-red-600 transition-all"
                    >
                      <PlusIcon />
                      <span className="ml-2">Ajouter une dépense</span>
                    </button>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'materials' && (
              <div className="space-y-6">
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Gestion des Matériaux</h3>
                  <div className="flex flex-col sm:flex-row sm:space-x-3 mobile-buttons">
                    <select
                      className="px-3 py-2 border border-orange-300 rounded-md text-sm focus:ring-2 focus:ring-orange-500 mb-2 sm:mb-0"
                      value={selectedTrancheId || ''}
                      onChange={(e) => {
                        const trancheId = e.target.value;
                        setSelectedTrancheId(trancheId);
                        if (trancheId) {
                          fetchMaterials(trancheId);
                          setSelectedTranche(tranches.find(t => t.id === trancheId));
                        } else {
                          setMaterials([]);
                          setSelectedTranche(null);
                        }
                      }}
                    >
                      <option value="">Sélectionner une tranche</option>
                      {tranches.map(tranche => (
                        <option key={tranche.id} value={tranche.id}>{tranche.name}</option>
                      ))}
                    </select>
                    {selectedTrancheId && (
                      <>
                        <button
                          onClick={() => setShowMaterialForm(true)}
                          className="inline-flex items-center px-3 py-2 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-md hover:from-orange-600 hover:to-red-600 transition-all text-sm"
                        >
                          <PlusIcon />
                          <span className="ml-1">Nouveau</span>
                        </button>
                        <div className="relative">
                          <input
                            type="file"
                            accept=".xlsx,.xls"
                            onChange={(e) => {
                              if (e.target.files[0]) {
                                handleImportMaterials(selectedTrancheId, e.target.files[0]);
                              }
                            }}
                            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                          />
                          <button className="inline-flex items-center px-3 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 transition-all text-sm w-full sm:w-auto">
                            <UploadIcon />
                            <span className="ml-1">Import</span>
                          </button>
                        </div>
                        <button
                          onClick={async () => {
                            if (window.confirm('Êtes-vous sûr de vouloir supprimer tous les matériaux de cette tranche ? Cette action est irréversible.')) {
                              try {
                                await deleteAllMaterials(project.id, selectedTrancheId);
                                setMaterials([]);
                                alert('Tous les matériaux ont été supprimés avec succès.');
                              } catch (error) {
                                alert('Erreur lors de la suppression des matériaux.');
                              }
                            }
                          }}
                          className="inline-flex items-center px-3 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-all text-sm"
                        >
                          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                          Supprimer tout
                        </button>
                        <button
                          onClick={async () => {
                            try {
                              const duplicates = await checkDuplicates(project.id, selectedTrancheId);
                              if (duplicates.has_duplicates) {
                                setDuplicatesData({
                                  expenses: duplicates.duplicate_expenses,
                                  materials: duplicates.duplicate_materials,
                                  trancheName: selectedTranche?.name || 'Tranche'
                                });
                                setShowDuplicatesModal(true);
                              } else {
                                alert('Aucun doublon détecté.');
                              }
                            } catch (error) {
                              alert('Erreur lors de la vérification des doublons.');
                            }
                          }}
                          className="inline-flex items-center px-3 py-2 bg-yellow-500 text-white rounded-md hover:bg-yellow-600 transition-all text-sm"
                        >
                          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          Vérifier doublons
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {!selectedTrancheId && (
                  <div className="text-center py-12">
                    <div className="text-gray-500 text-lg mb-4">Sélectionnez une tranche pour voir les matériaux</div>
                  </div>
                )}

                {selectedTrancheId && (
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <h4 className="font-medium text-blue-900 mb-2">{selectedTranche?.name}</h4>
                    <p className="text-sm text-blue-700 mb-2">
                      Budget: {formatAmount(selectedTranche?.budget_planned || 0)} Ar
                    </p>
                    <div className="text-xs text-blue-600 bg-blue-100 p-2 rounded">
                      💡 <strong>Format Excel attendu:</strong> DESIGNATIONS, UNITÉ, QUANTITÉ, PU, MONTANT
                    </div>
                  </div>
                )}

                {materials.length > 0 && (
                  <div className="bg-white border rounded-lg overflow-hidden">
                    <div className="px-6 py-4 border-b bg-gray-50">
                      <h4 className="font-medium text-gray-900">Matériaux prévisionnels</h4>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="min-w-full materials-table divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Désignation
                            </th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Unité
                            </th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Quantité
                            </th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              PU (Ar)
                            </th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Montant (Ar)
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {materials.map((material) => (
                            <tr key={material.id}>
                              <td className="text-xs border-r border-gray-200">
                                <EditableCell
                                  value={material.designation}
                                  onSave={(value) => handleUpdateMaterial(material.id, 'designation', value)}
                                />
                              </td>
                              <td className="text-xs border-r border-gray-200">
                                <EditableCell
                                  value={material.unit}
                                  onSave={(value) => handleUpdateMaterial(material.id, 'unit', value)}
                                />
                              </td>
                              <td className="text-xs border-r border-gray-200">
                                <EditableCell
                                  value={material.quantity_planned}
                                  onSave={(value) => handleUpdateMaterial(material.id, 'quantity_planned', value)}
                                  type="number"
                                />
                              </td>
                              <td className="text-xs border-r border-gray-200">
                                <EditableCell
                                  value={formatAmount(material.unit_price)}
                                  onSave={(value) => handleUpdateMaterial(material.id, 'unit_price', value)}
                                />
                              </td>
                              <td className="text-xs">
                                <EditableCell
                                  value={formatAmount(material.amount_planned)}
                                  onSave={(value) => handleUpdateMaterial(material.id, 'amount_planned', value)}
                                  disabled={true}
                                />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot className="bg-gray-50">
                          <tr>
                            <td colSpan="4" className="px-6 py-4 text-sm font-medium text-gray-900">
                              Total des matériaux
                            </td>
                            <td className="px-6 py-4 text-sm font-bold text-gray-900">
                              {formatAmount(materials.reduce((sum, material) => sum + material.amount_planned, 0))} Ar
                            </td>
                          </tr>
                          <tr>
                            <td colSpan="4" className="px-6 py-4 text-sm font-medium text-blue-900">
                              Budget de la tranche
                            </td>
                            <td className="px-6 py-4 text-sm font-bold text-blue-900">
                              {formatAmount(selectedTranche?.budget_planned || 0)} Ar
                            </td>
                          </tr>
                          <tr>
                            <td colSpan="4" className="px-6 py-4 text-sm font-medium">
                              Différence
                            </td>
                            <td className={`px-6 py-4 text-sm font-bold ${
                              (selectedTranche?.budget_planned || 0) - materials.reduce((sum, material) => sum + material.amount_planned, 0) >= 0 
                                ? 'text-green-600' 
                                : 'text-red-600'
                            }`}>
                              {(() => {
                                const diff = (selectedTranche?.budget_planned || 0) - materials.reduce((sum, material) => sum + material.amount_planned, 0);
                                return `${diff >= 0 ? '+' : ''}${formatAmount(diff)} Ar`;
                              })()}
                            </td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </div>
                )}

                {selectedTrancheId && materials.length === 0 && (
                  <div className="text-center py-12">
                    <div className="text-gray-500 text-lg mb-4">Aucun matériau pour cette tranche</div>
                    <button
                      onClick={() => setShowMaterialForm(true)}
                      className="px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                    >
                      Ajouter un matériau
                    </button>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'orders' && canAccessTab('orders') && (
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-medium text-gray-900">Gestion des Commandes</h3>
                  <div className="text-sm text-gray-500">
                    Commandes liées aux matériaux prévisionnels
                  </div>
                </div>

                {/* Sélecteur de tranche */}
                {!selectedTrancheId && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="text-blue-800 text-sm">
                      📋 Sélectionnez une tranche pour gérer les commandes
                    </p>
                  </div>
                )}

                {selectedTrancheId && (
                  <>
                    {/* Résumé des matériaux */}
                    <div className="bg-white border rounded-lg overflow-hidden">
                      <div className="px-6 py-3 border-b bg-gray-50">
                        <h4 className="font-medium text-gray-900">Résumé des Matériaux - {selectedTranche?.name}</h4>
                        <p className="text-sm text-gray-500">Quantités prévisionnelles vs commandées</p>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Matériau
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Unité
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Qté Prévue
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Qté Commandée
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Reste à Commander
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                % Commandé
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Actions
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {materialsSummary.map((material, index) => (
                              <tr key={index} className="hover:bg-gray-50">
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="font-medium text-gray-900">
                                    {material.designation}
                                  </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                  {material.unit}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                  {material.quantity_planned}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                  {material.quantity_ordered}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <span className={`text-sm font-medium ${
                                    material.quantity_remaining > 0 ? 'text-orange-600' : 'text-green-600'
                                  }`}>
                                    {material.quantity_remaining}
                                  </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="flex items-center">
                                    <div className="flex-1">
                                      <div className={`text-sm font-medium ${
                                        material.percentage_ordered >= 100 ? 'text-green-600' : 'text-gray-900'
                                      }`}>
                                        {material.percentage_ordered.toFixed(1)}%
                                      </div>
                                      <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                                        <div 
                                          className={`h-2 rounded-full ${
                                            material.percentage_ordered >= 100 ? 'bg-green-500' : 'bg-blue-500'
                                          }`}
                                          style={{ width: `${Math.min(material.percentage_ordered, 100)}%` }}
                                        ></div>
                                      </div>
                                    </div>
                                  </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                  {material.quantity_remaining > 0 && (
                                    <button
                                      onClick={() => {
                                        setSelectedMaterialForOrder(material);
                                        setShowOrderForm(true);
                                      }}
                                      className="text-blue-600 hover:text-blue-900"
                                    >
                                      Commander
                                    </button>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* Liste des commandes */}
                    <div className="bg-white border rounded-lg overflow-hidden">
                      <div className="px-6 py-3 border-b bg-gray-50">
                        <div className="flex justify-between items-center">
                          <h4 className="font-medium text-gray-900">Commandes en cours</h4>
                          <button
                            onClick={() => setShowOrderForm(true)}
                            className="inline-flex items-center px-3 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 text-sm"
                          >
                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                            </svg>
                            Nouvelle Commande
                          </button>
                        </div>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Matériau
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Quantité
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Fournisseur
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Date Commande
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Livraison Prévue
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Statut
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Total
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Actions
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {orders.map((order) => (
                              <tr key={order.id} className="hover:bg-gray-50">
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="font-medium text-gray-900">
                                    {order.material_designation}
                                  </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                  {order.quantity_ordered} {order.unit}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                  {order.supplier}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                  {formatDateEuropean(order.order_date)}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                  {formatDateEuropean(order.expected_delivery_date)}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <select
                                    value={order.status}
                                    onChange={(e) => handleUpdateOrderStatus(order.id, e.target.value)}
                                    className={`text-xs px-2 py-1 rounded-full ${
                                      order.status === 'Brouillon' ? 'bg-gray-100 text-gray-800' :
                                      order.status === 'Envoyée' ? 'bg-blue-100 text-blue-800' :
                                      order.status === 'Confirmée' ? 'bg-yellow-100 text-yellow-800' :
                                      order.status === 'Livrée' ? 'bg-green-100 text-green-800' :
                                      order.status === 'Payée' ? 'bg-purple-100 text-purple-800' :
                                      'bg-gray-100 text-gray-800'
                                    }`}
                                  >
                                    <option value="Brouillon">Brouillon</option>
                                    <option value="Envoyée">Envoyée</option>
                                    <option value="Confirmée">Confirmée</option>
                                    <option value="Livrée">Livrée</option>
                                    <option value="Payée">Payée</option>
                                  </select>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                  {formatAmount(order.total_amount)} Ar
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                  <button
                                    onClick={() => handleDeleteOrder(order.id)}
                                    className="text-red-600 hover:text-red-900"
                                  >
                                    Supprimer
                                  </button>
                                </td>
                              </tr>
                            ))}
                            {orders.length === 0 && (
                              <tr>
                                <td colSpan="8" className="px-6 py-4 text-center text-gray-500">
                                  Aucune commande trouvée pour cette tranche
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}

            {activeTab === 'comparison' && canAccessTab('comparison') && (
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-medium text-gray-900">Comparaison Matériaux - Prévisionnel vs Réel</h3>
                  <div className="text-sm text-gray-500">
                    Regroupement automatique des matériaux
                  </div>
                </div>

                <div className="bg-white border rounded-lg overflow-hidden">
                  <div className="px-6 py-3 border-b bg-gray-50">
                    <h4 className="font-medium text-gray-900">Comparaison des Matériaux</h4>
                    <p className="text-sm text-gray-500">Cliquez sur une ligne pour voir les détails</p>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr className="text-xs">
                          <th className="px-4 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">
                            Matériau
                          </th>
                          <th className="px-4 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">
                            Unité
                          </th>
                          <th className="px-4 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">
                            Qté Prévue
                          </th>
                          <th className="px-4 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">
                            PU Prévu (Ar)
                          </th>
                          <th className="px-4 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">
                            Montant Prévu (Ar)
                          </th>
                          <th className="px-4 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">
                            Qté Réelle
                          </th>
                          <th className="px-4 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">
                            PU Réel (Ar)
                          </th>
                          <th className="px-4 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">
                            Montant Réel (Ar)
                          </th>
                          <th className="px-4 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">
                            Écart (Ar)
                          </th>
                          <th className="px-4 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">
                            % Écart
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {/* Ligne de totaux en haut */}
                        <tr className="bg-blue-50 font-medium text-sm">
                          <td className="px-4 py-2 text-blue-900">
                            <strong>TOTAUX</strong>
                          </td>
                          <td className="px-4 py-2 text-blue-900">-</td>
                          <td className="px-4 py-2 text-blue-900">-</td>
                          <td className="px-4 py-2 text-blue-900">-</td>
                          <td className="px-4 py-2 text-blue-900">
                            <strong>
                              {formatAmount(
                                tranches.reduce((total, tranche) => 
                                  total + (tranche.materials || []).reduce((sum, material) => sum + material.amount_planned, 0), 0
                                )
                              )} Ar
                            </strong>
                          </td>
                          <td className="px-4 py-2 text-blue-900">-</td>
                          <td className="px-4 py-2 text-blue-900">-</td>
                          <td className="px-4 py-2 text-blue-900">
                            <strong>
                              {formatAmount(
                                tranches.reduce((total, tranche) => 
                                  total + (tranche.expenses || []).reduce((sum, expense) => sum + expense.amount, 0), 0
                                )
                              )} Ar
                            </strong>
                          </td>
                          <td className={`px-4 py-2 font-bold ${
                            tranches.reduce((total, tranche) => 
                              total + (tranche.expenses || []).reduce((sum, expense) => sum + expense.amount, 0), 0
                            ) -
                            tranches.reduce((total, tranche) => 
                              total + (tranche.materials || []).reduce((sum, material) => sum + material.amount_planned, 0), 0
                            ) >= 0 ? 'text-red-600' : 'text-green-600'
                          }`}>
                            {(() => {
                              const totalActual = tranches.reduce((total, tranche) => 
                                total + (tranche.expenses || []).reduce((sum, expense) => sum + expense.amount, 0), 0
                              );
                              const totalPlanned = tranches.reduce((total, tranche) => 
                                total + (tranche.materials || []).reduce((sum, material) => sum + material.amount_planned, 0), 0
                              );
                              const diff = totalActual - totalPlanned;
                              return `${diff >= 0 ? '+' : ''}${formatAmount(diff)}`;
                            })()}
                          </td>
                          <td className={`px-4 py-2 font-bold ${
                            tranches.reduce((total, tranche) => 
                              total + (tranche.expenses || []).reduce((sum, expense) => sum + expense.amount, 0), 0
                            ) -
                            tranches.reduce((total, tranche) => 
                              total + (tranche.materials || []).reduce((sum, material) => sum + material.amount_planned, 0), 0
                            ) >= 0 ? 'text-red-600' : 'text-green-600'
                          }`}>
                            {(() => {
                              const totalActual = tranches.reduce((total, tranche) => 
                                total + (tranche.expenses || []).reduce((sum, expense) => sum + expense.amount, 0), 0
                              );
                              const totalPlanned = tranches.reduce((total, tranche) => 
                                total + (tranche.materials || []).reduce((sum, material) => sum + material.amount_planned, 0), 0
                              );
                              const percentage = totalPlanned > 0 ? 
                                ((totalActual - totalPlanned) / totalPlanned) * 100 : 0;
                              return `${percentage >= 0 ? '+' : ''}${percentage.toFixed(1)}%`;
                            })()}
                          </td>
                        </tr>
                        
                        {(() => {
                          // Regrouper les matériaux par nom commun
                          const materialGroups = {};
                          const allExpenses = tranches.flatMap(tranche => tranche.expenses || []);
                          
                          tranches.forEach(tranche => {
                            const trancheMaterials = tranche.materials || [];
                            
                            trancheMaterials.forEach(material => {
                              // Normaliser le nom du matériau
                              const normalizedName = material.designation.toLowerCase()
                                .replace(/\s+/g, ' ')
                                .replace(/portland|de\s+rivière|6mm|8mm|10mm|12mm|14mm/g, '')
                                .trim();
                              
                              let commonName = material.designation;
                              
                              // Distinction précise des types de fer
                              if (normalizedName.includes('fer') || normalizedName.includes('ha')) {
                                // Extraire le diamètre du fer
                                const ferMatch = normalizedName.match(/fer\s*(\d+)|ha\s*(\d+)|φ\s*(\d+)|ø\s*(\d+)|diameter\s*(\d+)/i);
                                if (ferMatch) {
                                  const diameter = ferMatch[1] || ferMatch[2] || ferMatch[3] || ferMatch[4] || ferMatch[5];
                                  commonName = `FER ${diameter}`;
                                } else {
                                  commonName = 'FER';
                                }
                              }
                              // Autres matériaux
                              else if (normalizedName.includes('ciment')) commonName = 'CIMENT';
                              else if (normalizedName.includes('sable')) commonName = 'SABLE';
                              else if (normalizedName.includes('gravillon')) commonName = 'GRAVILLON';
                              else if (normalizedName.includes('planche')) commonName = 'PLANCHE';
                              else if (normalizedName.includes('bois')) commonName = 'BOIS';
                              else if (normalizedName.includes('pointe')) commonName = 'POINTE';
                              else if (normalizedName.includes('fil')) commonName = 'FIL RECUIT';
                              
                              if (!materialGroups[commonName]) {
                                materialGroups[commonName] = {
                                  commonName,
                                  unit: material.unit,
                                  quantityPlanned: 0,
                                  quantityActual: 0,
                                  amountPlanned: 0,
                                  amountActual: 0,
                                  relatedExpenses: [],
                                  detailedMaterials: []
                                };
                              }
                              
                              materialGroups[commonName].quantityPlanned += material.quantity_planned;
                              materialGroups[commonName].amountPlanned += material.amount_planned;
                              materialGroups[commonName].detailedMaterials.push(material);
                              
                              // Chercher les dépenses qui correspondent à ce matériau
                              const relatedExpenses = allExpenses.filter(expense => {
                                const expenseDesc = expense.description.toLowerCase();
                                const materialName = material.designation.toLowerCase();
                                return expenseDesc.includes(materialName) || 
                                       materialName.includes(expenseDesc) ||
                                       expenseDesc.includes(normalizedName) ||
                                       normalizedName.includes(expenseDesc);
                              });
                              
                              relatedExpenses.forEach(expense => {
                                if (!materialGroups[commonName].relatedExpenses.find(e => e.id === expense.id)) {
                                  materialGroups[commonName].relatedExpenses.push(expense);
                                  materialGroups[commonName].amountActual += expense.amount;
                                  materialGroups[commonName].quantityActual += expense.quantity;
                                }
                              });
                            });
                          });
                          
                          return Object.values(materialGroups).map(group => {
                            // Calculer les prix unitaires moyens
                            const avgPlannedUnitPrice = group.quantityPlanned > 0 ? 
                              group.amountPlanned / group.quantityPlanned : 0;
                            const avgActualUnitPrice = group.quantityActual > 0 ? 
                              group.amountActual / group.quantityActual : 0;
                            
                            // Calculer les montants cohérents (prix unitaire moyen × quantité)
                            const calculatedPlannedAmount = avgPlannedUnitPrice * group.quantityPlanned;
                            const calculatedActualAmount = avgActualUnitPrice * group.quantityActual;
                            
                            // Utiliser les montants calculés pour l'écart
                            const gap = calculatedActualAmount - calculatedPlannedAmount;
                            const percentage = calculatedPlannedAmount > 0 ? 
                              (gap / calculatedPlannedAmount) * 100 : 0;
                            
                            return (
                              <React.Fragment key={group.commonName}>
                                <tr 
                                  className="hover:bg-gray-50 cursor-pointer text-sm"
                                  onClick={() => {
                                    const detailsRow = document.getElementById(`details-${group.commonName}`);
                                    if (detailsRow) {
                                      detailsRow.style.display = detailsRow.style.display === 'none' ? 'table-row' : 'none';
                                    }
                                  }}
                                >
                                  <td className="px-4 py-2 font-medium text-gray-900">
                                    <div className="flex items-center">
                                      <span className="mr-2">▶</span>
                                      {group.commonName}
                                    </div>
                                  </td>
                                  <td className="px-4 py-2 text-gray-900">
                                    {group.unit}
                                  </td>
                                  <td className="px-4 py-2 text-gray-900">
                                    {group.quantityPlanned}
                                  </td>
                                  <td className="px-4 py-2 text-gray-900">
                                    {formatAmount(avgPlannedUnitPrice)}
                                  </td>
                                  <td className="px-4 py-2 text-gray-900">
                                    {formatAmount(calculatedPlannedAmount)}
                                  </td>
                                  <td className="px-4 py-2 text-gray-900">
                                    {group.quantityActual}
                                  </td>
                                  <td className="px-4 py-2 text-gray-900">
                                    {group.quantityActual > 0 ? formatAmount(avgActualUnitPrice) : '-'}
                                  </td>
                                  <td className="px-4 py-2 text-gray-900">
                                    {formatAmount(calculatedActualAmount)}
                                  </td>
                                  <td className={`px-4 py-2 font-medium ${gap >= 0 ? 'text-red-600' : 'text-green-600'}`}>
                                    {gap >= 0 ? '+' : ''}{formatAmount(gap)}
                                  </td>
                                  <td className={`px-4 py-2 ${percentage >= 0 ? 'text-red-600' : 'text-green-600'}`}>
                                    {percentage >= 0 ? '+' : ''}{percentage.toFixed(1)}%
                                  </td>
                                </tr>
                                <tr 
                                  id={`details-${group.commonName}`} 
                                  className="bg-gray-50"
                                  style={{ display: 'none' }}
                                >
                                  <td colSpan="10" className="px-4 py-2">
                                    <div className="text-xs text-gray-600 space-y-1">
                                      <div><strong>Matériaux prévisionnels:</strong></div>
                                      {group.detailedMaterials.map((material, index) => (
                                        <div key={index} className="ml-4">
                                          • {material.designation} - {material.quantity_planned} {material.unit} à {formatAmount(material.unit_price)} Ar = {formatAmount(material.amount_planned)} Ar
                                        </div>
                                      ))}
                                      {group.relatedExpenses.length > 0 && (
                                        <>
                                          <div className="mt-2"><strong>Dépenses trouvées:</strong></div>
                                          {group.relatedExpenses.map((expense, index) => (
                                            <div key={index} className="ml-4">
                                              • {formatDateEuropean(expense.date)} - {expense.description} - {expense.quantity} à {formatAmount(expense.unit_price)} Ar = {formatAmount(expense.amount)} Ar
                                            </div>
                                          ))}
                                        </>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              </React.Fragment>
                            );
                          });
                        })()}
                      </tbody>
                      <tfoot className="bg-gray-50">
                        <tr className="font-medium text-sm">
                          <td className="px-4 py-2 text-gray-900">
                            <strong>TOTAUX</strong>
                          </td>
                          <td className="px-4 py-2 text-gray-900">-</td>
                          <td className="px-4 py-2 text-gray-900">-</td>
                          <td className="px-4 py-2 text-gray-900">-</td>
                          <td className="px-4 py-2 font-bold text-gray-900">
                            {formatAmount(
                              tranches.reduce((total, tranche) => 
                                total + (tranche.materials || []).reduce((sum, material) => sum + material.amount_planned, 0), 0
                              )
                            )} Ar
                          </td>
                          <td className="px-4 py-2 text-gray-900">-</td>
                          <td className="px-4 py-2 text-gray-900">-</td>
                          <td className="px-4 py-2 font-bold text-gray-900">
                            {formatAmount(
                              tranches.reduce((total, tranche) => 
                                total + (tranche.expenses || []).reduce((sum, expense) => sum + expense.amount, 0), 0
                              )
                            )} Ar
                          </td>
                          <td className={`px-4 py-2 font-bold ${
                            tranches.reduce((total, tranche) => 
                              total + (tranche.expenses || []).reduce((sum, expense) => sum + expense.amount, 0), 0
                            ) -
                            tranches.reduce((total, tranche) => 
                              total + (tranche.materials || []).reduce((sum, material) => sum + material.amount_planned, 0), 0
                            ) >= 0 ? 'text-red-600' : 'text-green-600'
                          }`}>
                            {(() => {
                              const totalActual = tranches.reduce((total, tranche) => 
                                total + (tranche.expenses || []).reduce((sum, expense) => sum + expense.amount, 0), 0
                              );
                              const totalPlanned = tranches.reduce((total, tranche) => 
                                total + (tranche.materials || []).reduce((sum, material) => sum + material.amount_planned, 0), 0
                              );
                              const diff = totalActual - totalPlanned;
                              return `${diff >= 0 ? '+' : ''}${formatAmount(diff)} Ar`;
                            })()}
                          </td>
                          <td className={`px-4 py-2 font-bold ${
                            tranches.reduce((total, tranche) => 
                              total + (tranche.expenses || []).reduce((sum, expense) => sum + expense.amount, 0), 0
                            ) -
                            tranches.reduce((total, tranche) => 
                              total + (tranche.materials || []).reduce((sum, material) => sum + material.amount_planned, 0), 0
                            ) >= 0 ? 'text-red-600' : 'text-green-600'
                          }`}>
                            {(() => {
                              const totalActual = tranches.reduce((total, tranche) => 
                                total + (tranche.expenses || []).reduce((sum, expense) => sum + expense.amount, 0), 0
                              );
                              const totalPlanned = tranches.reduce((total, tranche) => 
                                total + (tranche.materials || []).reduce((sum, material) => sum + material.amount_planned, 0), 0
                              );
                              const percentage = totalPlanned > 0 ? 
                                ((totalActual - totalPlanned) / totalPlanned) * 100 : 0;
                              return `${percentage >= 0 ? '+' : ''}${percentage.toFixed(1)}%`;
                            })()}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>

                {/* Comparaison détaillée par tranche */}
                <div className="space-y-6">
                  <div className="flex justify-between items-center">
                    <h4 className="font-medium text-gray-900">Comparaison Détaillée par Tranche</h4>
                    <p className="text-sm text-gray-500">Affichage des tranches payées uniquement</p>
                  </div>
                  
                  {tranches.filter(tranche => tranche.is_paid).map((tranche) => {
                    const trancheMaterials = tranche.materials || [];
                    const trancheExpenses = tranche.expenses || [];
                    
                    if (trancheMaterials.length === 0) {
                      return null;
                    }
                    
                    // Regrouper les matériaux par nom commun pour cette tranche
                    const materialGroups = {};
                    
                    trancheMaterials.forEach(material => {
                      // Normaliser le nom du matériau
                      const normalizedName = material.designation.toLowerCase()
                        .replace(/\s+/g, ' ')
                        .replace(/portland|de\s+rivière|6mm|8mm|10mm|12mm|14mm/g, '')
                        .trim();
                      
                      let commonName = material.designation;
                      
                      // Distinction précise des types de fer
                      if (normalizedName.includes('fer') || normalizedName.includes('ha')) {
                        // Vérifier que c'est vraiment du fer et non un mot contenant "fer"
                        const ferWords = ['fer', 'fers', 'ha', 'acier', 'barre', 'rond'];
                        const hasFerWord = ferWords.some(word => {
                          const regex = new RegExp(`\\b${word}\\b`, 'i');
                          return regex.test(material.designation);
                        });
                        
                        if (hasFerWord) {
                          // Extraire le diamètre du fer
                          const ferMatch = normalizedName.match(/fer\s*(\d+)|ha\s*(\d+)|φ\s*(\d+)|ø\s*(\d+)|diameter\s*(\d+)/i);
                          if (ferMatch) {
                            const diameter = ferMatch[1] || ferMatch[2] || ferMatch[3] || ferMatch[4] || ferMatch[5];
                            commonName = `FER ${diameter}`;
                          } else {
                            commonName = 'FER';
                          }
                        } else {
                          commonName = material.designation; // Garder le nom original si ce n'est pas du fer
                        }
                      }
                      // Autres matériaux
                      else if (normalizedName.includes('ciment')) commonName = 'CIMENT';
                      else if (normalizedName.includes('sable')) commonName = 'SABLE';
                      else if (normalizedName.includes('gravillon')) commonName = 'GRAVILLON';
                      else if (normalizedName.includes('planche')) commonName = 'PLANCHE';
                      else if (normalizedName.includes('bois')) commonName = 'BOIS';
                      else if (normalizedName.includes('pointe')) commonName = 'POINTE';
                      else if (normalizedName.includes('fil')) commonName = 'FIL RECUIT';
                      else {
                        // Si aucune catégorie ne correspond, garder le nom original
                        commonName = material.designation;
                      }
                      
                      if (!materialGroups[commonName]) {
                        materialGroups[commonName] = {
                          commonName,
                          unit: material.unit,
                          quantityPlanned: 0,
                          quantityActual: 0,
                          amountPlanned: 0,
                          amountActual: 0,
                          relatedExpenses: [],
                          detailedMaterials: []
                        };
                      }
                      
                      materialGroups[commonName].quantityPlanned += material.quantity_planned;
                      materialGroups[commonName].amountPlanned += material.amount_planned;
                      materialGroups[commonName].detailedMaterials.push(material);
                    });
                    
                    // Maintenant, pour chaque groupe, chercher les dépenses correspondantes
                    Object.keys(materialGroups).forEach(groupName => {
                      const group = materialGroups[groupName];
                      
                      // Chercher les dépenses correspondantes pour ce groupe
                      const groupExpenses = trancheExpenses.filter(expense => {
                        const expenseDesc = expense.description.toLowerCase();
                        
                        // Vérifier si cette dépense correspond à ce groupe
                        if (groupName.startsWith('FER')) {
                          // Pour le fer, vérifier que l'expense contient vraiment des mots liés au fer
                          const ferWords = ['fer', 'fers', 'ha', 'acier', 'barre'];
                          const hasFerInExpense = ferWords.some(word => {
                            const regex = new RegExp(`\\b${word}\\b`, 'i');
                            return regex.test(expense.description);
                          });
                          
                          if (hasFerInExpense) {
                            // Vérifier le diamètre si spécifié
                            const groupDiameter = groupName.match(/FER (\d+)/);
                            const expenseDiameter = expense.description.match(/\b(\d+)(?:mm)?\b/);
                            if (groupDiameter && expenseDiameter) {
                              return groupDiameter[1] === expenseDiameter[1];
                            }
                            return !groupDiameter; // Si le groupe n'a pas de diamètre spécifique, accepter
                          }
                        }
                        // Pour autres matériaux, correspondance par mot-clé
                        else if (groupName === 'CIMENT' && expenseDesc.includes('ciment')) return true;
                        else if (groupName === 'SABLE' && expenseDesc.includes('sable')) return true;
                        else if (groupName === 'GRAVILLON' && (expenseDesc.includes('gravillon') || expenseDesc.includes('gravier'))) return true;
                        else if (groupName === 'PLANCHE' && (expenseDesc.includes('planche') || expenseDesc.includes('bois'))) return true;
                        else if (groupName === 'BOIS' && expenseDesc.includes('bois')) return true;
                        else if (groupName === 'POINTE' && (expenseDesc.includes('pointe') || expenseDesc.includes('clou'))) return true;
                        else if (groupName === 'FIL RECUIT' && expenseDesc.includes('fil')) return true;
                        else {
                          // Pour les groupes avec nom original, correspondance exacte ou partielle
                          return expenseDesc.includes(groupName.toLowerCase()) || groupName.toLowerCase().includes(expenseDesc);
                        }
                        
                        return false;
                      });
                      
                      group.relatedExpenses = groupExpenses;
                      group.quantityActual = groupExpenses.reduce((sum, expense) => sum + expense.quantity, 0);
                      group.amountActual = groupExpenses.reduce((sum, expense) => sum + expense.amount, 0);
                    });
                    
                    return (
                      <div key={tranche.id} className="bg-white border rounded-lg overflow-hidden">
                        <div className="px-6 py-3 border-b bg-gradient-to-r from-blue-50 to-indigo-50">
                          <div className="flex justify-between items-center">
                            <div>
                              <h5 className="font-medium text-gray-900">{tranche.name}</h5>
                              <p className="text-sm text-gray-600">Budget: {formatAmount(tranche.budget_planned)} Ar</p>
                            </div>
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              Payée
                            </span>
                          </div>
                        </div>
                        <div className="overflow-x-auto">
                          <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                              <tr className="text-xs">
                                <th className="px-4 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">
                                  Matériau
                                </th>
                                <th className="px-4 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">
                                  Unité
                                </th>
                                <th className="px-4 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">
                                  Qté Prévue
                                </th>
                                <th className="px-4 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">
                                  Prix Unit. Prévu (Ar)
                                </th>
                                <th className="px-4 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">
                                  Montant Prévu (Ar)
                                </th>
                                <th className="px-4 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">
                                  Qté Réelle
                                </th>
                                <th className="px-4 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">
                                  Prix Unit. Réel (Ar)
                                </th>
                                <th className="px-4 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">
                                  Montant Réel (Ar)
                                </th>
                                <th className="px-4 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">
                                  Écart (Ar)
                                </th>
                                <th className="px-4 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">
                                  % Écart
                                </th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              {/* Total row for this tranche */}
                              <tr className="bg-blue-50 font-medium text-sm">
                                <td className="px-4 py-2 text-blue-900">
                                  <strong>TOTAL TRANCHE</strong>
                                </td>
                                <td className="px-4 py-2 text-blue-900">-</td>
                                <td className="px-4 py-2 text-blue-900">-</td>
                                <td className="px-4 py-2 text-blue-900">-</td>
                                <td className="px-4 py-2 text-blue-900">
                                  <strong>
                                    {formatAmount(trancheMaterials.reduce((sum, mat) => sum + mat.amount_planned, 0))} Ar
                                  </strong>
                                </td>
                                <td className="px-4 py-2 text-blue-900">
                                  <strong>
                                    {formatAmount(trancheExpenses.reduce((sum, expense) => sum + expense.amount, 0))} Ar
                                  </strong>
                                </td>
                                <td className="px-4 py-2 text-blue-900">
                                  {(() => {
                                    const totalPlanned = trancheMaterials.reduce((sum, mat) => sum + mat.amount_planned, 0);
                                    const totalActual = trancheExpenses.reduce((sum, expense) => sum + expense.amount, 0);
                                    const diff = totalActual - totalPlanned;
                                    return (
                                      <span className={`font-bold ${diff >= 0 ? 'text-red-600' : 'text-green-600'}`}>
                                        {diff >= 0 ? '+' : ''}{formatAmount(diff)} Ar
                                      </span>
                                    );
                                  })()}
                                </td>
                                <td className="px-4 py-2 text-blue-900">
                                  {(() => {
                                    const totalPlanned = trancheMaterials.reduce((sum, mat) => sum + mat.amount_planned, 0);
                                    const totalActual = trancheExpenses.reduce((sum, expense) => sum + expense.amount, 0);
                                    const percentage = totalPlanned > 0 ? ((totalActual - totalPlanned) / totalPlanned) * 100 : 0;
                                    return (
                                      <span className={`font-bold ${percentage >= 0 ? 'text-red-600' : 'text-green-600'}`}>
                                        {percentage >= 0 ? '+' : ''}{percentage.toFixed(1)}%
                                      </span>
                                    );
                                  })()}
                                </td>
                              </tr>
                              
                              {/* Material groups with clickable details */}
                              {Object.values(materialGroups).map((group, index) => {
                                // Calculer les prix unitaires moyens
                                const avgPlannedUnitPrice = group.quantityPlanned > 0 ? 
                                  group.amountPlanned / group.quantityPlanned : 0;
                                const avgActualUnitPrice = group.quantityActual > 0 ? 
                                  group.amountActual / group.quantityActual : 0;
                                
                                // Calculer les montants cohérents (prix unitaire moyen × quantité)
                                const calculatedPlannedAmount = avgPlannedUnitPrice * group.quantityPlanned;
                                const calculatedActualAmount = avgActualUnitPrice * group.quantityActual;
                                
                                // Utiliser les montants calculés pour l'écart
                                const gap = calculatedActualAmount - calculatedPlannedAmount;
                                const percentage = calculatedPlannedAmount > 0 ? 
                                  (gap / calculatedPlannedAmount) * 100 : 0;
                                
                                return (
                                  <React.Fragment key={index}>
                                    <tr 
                                      className="hover:bg-gray-50 cursor-pointer"
                                      onClick={() => {
                                        const detailsRow = document.getElementById(`details-${tranche.id}-${group.commonName}`);
                                        if (detailsRow) {
                                          detailsRow.style.display = detailsRow.style.display === 'none' ? 'table-row' : 'none';
                                        }
                                      }}
                                    >
                                      <td className="px-4 py-2 font-medium text-gray-900">
                                        <div className="flex items-center">
                                          <span className="mr-2">▶</span>
                                          {group.commonName}
                                        </div>
                                      </td>
                                      <td className="px-4 py-2 text-gray-900">
                                        {group.unit}
                                      </td>
                                      <td className="px-4 py-2 text-gray-900">
                                        {group.quantityPlanned}
                                      </td>
                                      <td className="px-4 py-2 text-gray-900">
                                        {formatAmount(avgPlannedUnitPrice)}
                                      </td>
                                      <td className="px-4 py-2 text-gray-900">
                                        {formatAmount(calculatedPlannedAmount)}
                                      </td>
                                      <td className="px-4 py-2 text-gray-900">
                                        {group.quantityActual}
                                      </td>
                                      <td className="px-4 py-2 text-gray-900">
                                        {group.quantityActual > 0 ? formatAmount(avgActualUnitPrice) : '-'}
                                      </td>
                                      <td className="px-4 py-2 text-gray-900">
                                        {formatAmount(calculatedActualAmount)}
                                      </td>
                                      <td className={`px-4 py-2 font-medium ${gap >= 0 ? 'text-red-600' : 'text-green-600'}`}>
                                        {gap >= 0 ? '+' : ''}{formatAmount(gap)}
                                      </td>
                                      <td className={`px-4 py-2 ${percentage >= 0 ? 'text-red-600' : 'text-green-600'}`}>
                                        {percentage >= 0 ? '+' : ''}{percentage.toFixed(1)}%
                                      </td>
                                    </tr>
                                    <tr 
                                      id={`details-${tranche.id}-${group.commonName}`}
                                      className="bg-gray-50"
                                      style={{ display: 'none' }}
                                    >
                                      <td colSpan="10" className="px-4 py-2">
                                        <div className="text-xs text-gray-600 space-y-1">
                                          <div><strong>Matériaux prévisionnels:</strong></div>
                                          {group.detailedMaterials.map((material, idx) => (
                                            <div key={idx} className="ml-4">
                                              • {material.designation} - {material.quantity_planned} {material.unit} à {formatAmount(material.unit_price)} Ar = {formatAmount(material.amount_planned)} Ar
                                            </div>
                                          ))}
                                          {group.relatedExpenses.length > 0 && (
                                            <>
                                              <div className="mt-2"><strong>Dépenses trouvées:</strong></div>
                                              {group.relatedExpenses.map((expense, idx) => (
                                                <div key={idx} className="ml-4">
                                                  • {formatDateEuropean(expense.date)} - {expense.description} - {expense.quantity} à {formatAmount(expense.unit_price)} Ar = {formatAmount(expense.amount)} Ar
                                                </div>
                                              ))}
                                            </>
                                          )}
                                        </div>
                                      </td>
                                    </tr>
                                  </React.Fragment>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    );
                  })}
                  
                  {tranches.filter(tranche => tranche.is_paid).length === 0 && (
                    <div className="text-center py-12">
                      <div className="text-gray-500 text-lg mb-4">Aucune tranche payée trouvée</div>
                      <p className="text-sm text-gray-400">
                        Marquez des tranches comme payées pour voir leur comparaison détaillée
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'salaires' && (
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-medium text-gray-900">Résumé des Salaires</h3>
                  <div className="text-sm text-gray-500">
                    Extrait automatiquement des dépenses
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-blue-50 p-6 rounded-lg">
                    <h4 className="text-lg font-medium text-blue-900 mb-2">Total Salaires</h4>
                    <p className="text-2xl font-bold text-blue-600">
                      {formatAmount(
                        tranches.reduce((total, tranche) => {
                          const salaryExpenses = (tranche.expenses || []).filter(expense => 
                            expense.description.toLowerCase().includes('salaire') ||
                            expense.description.toLowerCase().includes('salary') ||
                            expense.description.toLowerCase().includes('équipe') ||
                            expense.description.toLowerCase().includes('equipe')
                          );
                          return total + salaryExpenses.reduce((sum, expense) => sum + expense.amount, 0);
                        }, 0)
                      )} Ar
                    </p>
                  </div>

                  <div className="bg-green-50 p-6 rounded-lg">
                    <h4 className="text-lg font-medium text-green-900 mb-2">Nombre de Paiements</h4>
                    <p className="text-2xl font-bold text-green-600">
                      {tranches.reduce((total, tranche) => {
                        const salaryExpenses = (tranche.expenses || []).filter(expense => 
                          expense.description.toLowerCase().includes('salaire') ||
                          expense.description.toLowerCase().includes('salary') ||
                          expense.description.toLowerCase().includes('équipe') ||
                          expense.description.toLowerCase().includes('equipe')
                        );
                        return total + salaryExpenses.length;
                      }, 0)}
                    </p>
                  </div>
                </div>

                <div className="bg-white border rounded-lg overflow-hidden">
                  <div className="px-6 py-4 border-b bg-gray-50">
                    <h4 className="font-medium text-gray-900">Détail des Salaires par Tranche</h4>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Tranche
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Total Salaires (Ar)
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Nombre de Paiements
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            % du Budget Tranche
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {tranches.map((tranche) => {
                          const salaryExpenses = (tranche.expenses || []).filter(expense => 
                            expense.description.toLowerCase().includes('salaire') ||
                            expense.description.toLowerCase().includes('salary') ||
                            expense.description.toLowerCase().includes('équipe') ||
                            expense.description.toLowerCase().includes('equipe')
                          );
                          const totalSalary = salaryExpenses.reduce((sum, expense) => sum + expense.amount, 0);
                          const percentage = tranche.budget_planned > 0 ? 
                            (totalSalary / tranche.budget_planned) * 100 : 0;
                          
                          return (
                            <tr key={tranche.id}>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                {tranche.name}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {formatAmount(totalSalary)}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {salaryExpenses.length}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {percentage.toFixed(1)}%
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="bg-white border rounded-lg overflow-hidden">
                  <div className="px-6 py-4 border-b bg-gray-50">
                    <h4 className="font-medium text-gray-900">Détail des Dépenses de Salaires</h4>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Date
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Tranche
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Description
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Montant (Ar)
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {tranches.flatMap(tranche => {
                          const salaryExpenses = (tranche.expenses || []).filter(expense => 
                            expense.description.toLowerCase().includes('salaire') ||
                            expense.description.toLowerCase().includes('salary') ||
                            expense.description.toLowerCase().includes('équipe') ||
                            expense.description.toLowerCase().includes('equipe')
                          );
                          
                          return salaryExpenses.map(expense => (
                            <tr key={expense.id}>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {expense.date}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {tranche.name}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {expense.description}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                {formatAmount(expense.amount)}
                              </td>
                            </tr>
                          ));
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal pour afficher les doublons */}
      {showDuplicatesModal && duplicatesData && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => setShowDuplicatesModal(false)}
        >
          <div 
            className="bg-white rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-900">
                Doublons détectés - {duplicatesData.trancheName}
              </h2>
              <button
                onClick={() => setShowDuplicatesModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="space-y-6">
              {/* Dépenses dupliquées */}
              {duplicatesData.expenses.length > 0 && (
                <div>
                  <h3 className="text-lg font-medium text-red-900 mb-3">
                    Dépenses dupliquées - même article, même date, même quantité, même prix ({duplicatesData.expenses.length})
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 border border-gray-200">
                      <thead className="bg-red-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-red-900 uppercase tracking-wider">
                            Date
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-red-900 uppercase tracking-wider">
                            Description
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-red-900 uppercase tracking-wider">
                            Quantité
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-red-900 uppercase tracking-wider">
                            Prix unitaire
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-red-900 uppercase tracking-wider">
                            Montant
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {duplicatesData.expenses
                          .sort((a, b) => {
                            // Trier par date puis par description
                            if (a.date !== b.date) return new Date(a.date) - new Date(b.date);
                            return a.description.localeCompare(b.description);
                          })
                          .map((expense, index) => (
                          <tr key={expense.id || index} className="hover:bg-gray-50">
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                              {formatDateEuropean(expense.date)}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-900">
                              {expense.description}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                              {expense.quantity}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                              {formatAmount(expense.unit_price)} Ar
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                              {formatAmount(expense.amount)} Ar
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Matériaux dupliqués */}
              {duplicatesData.materials.length > 0 && (
                <div>
                  <h3 className="text-lg font-medium text-orange-900 mb-3">
                    Matériaux dupliqués - même désignation, même quantité, même prix ({duplicatesData.materials.length})
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 border border-gray-200">
                      <thead className="bg-orange-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-orange-900 uppercase tracking-wider">
                            Désignation
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-orange-900 uppercase tracking-wider">
                            Unité
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-orange-900 uppercase tracking-wider">
                            Quantité prévue
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-orange-900 uppercase tracking-wider">
                            Prix unitaire
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-orange-900 uppercase tracking-wider">
                            Montant prévu
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {duplicatesData.materials
                          .sort((a, b) => a.designation.localeCompare(b.designation))
                          .map((material, index) => (
                          <tr key={material.id || index} className="hover:bg-gray-50">
                            <td className="px-4 py-3 text-sm font-medium text-gray-900">
                              {material.designation}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                              {material.unit}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                              {material.quantity_planned}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                              {formatAmount(material.unit_price)} Ar
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                              {formatAmount(material.amount_planned)} Ar
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
            
            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setShowDuplicatesModal(false)}
                className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      {showOrderForm && (
        <OrderForm
          material={selectedMaterialForOrder}
          onSubmit={handleCreateOrder}
          onCancel={() => {
            setShowOrderForm(false);
            setSelectedMaterialForOrder(null);
          }}
        />
      )}

      {showProjectForm && (
        <ProjectForm
          project={project}
          onSubmit={handleUpdateProject}
          onCancel={() => setShowProjectForm(false)}
        />
      )}

      {showTrancheForm && (
        <TrancheForm
          tranche={editingItem}
          onSubmit={handleCreateTranche}
          onCancel={() => {
            setShowTrancheForm(false);
            setEditingItem(null);
          }}
        />
      )}

      {showExpenseForm && (
        <ExpenseForm
          expense={editingItem}
          onSubmit={handleCreateExpense}
          onCancel={() => {
            setShowExpenseForm(false);
            setEditingItem(null);
          }}
        />
      )}

      {showMaterialForm && (
        <MaterialForm
          material={editingItem}
          onSubmit={handleCreateMaterial}
          onCancel={() => {
            setShowMaterialForm(false);
            setEditingItem(null);
          }}
        />
      )}
    </div>
  );
};

// Composant pour le tableau de bord global
const GlobalDashboard = ({ projects, onProjectSelect }) => {
  const [globalStats, setGlobalStats] = useState({
    totalProjects: 0,
    totalBudget: 0,
    totalActual: 0,
    totalPaidTranches: 0,
    totalUnpaidTranches: 0,
    totalPaidBudget: 0,
    totalUnpaidBudget: 0,
    totalResult: 0,
    resultPercentage: 0
  });

  const [projectsWithStats, setProjectsWithStats] = useState([]);

  useEffect(() => {
    calculateGlobalStats();
  }, [projects]);

  const calculateGlobalStats = () => {
    if (!projects || projects.length === 0) return;

    let totalBudget = 0;
    let totalActual = 0;
    let totalPaidTranches = 0;
    let totalUnpaidTranches = 0;
    let totalPaidBudget = 0;
    let totalUnpaidBudget = 0;
    const projectStats = [];

    projects.forEach(project => {
      let projectBudget = project.total_budget || 0;
      let projectActual = 0;
      let projectPaidTranches = 0;
      let projectUnpaidTranches = 0;
      let projectPaidBudget = 0;
      let projectUnpaidBudget = 0;

      if (project.tranches && project.tranches.length > 0) {
        project.tranches.forEach(tranche => {
          const trancheBudget = tranche.budget_planned || 0;
          
          if (tranche.is_paid) {
            projectPaidTranches++;
            totalPaidTranches++;
            projectPaidBudget += trancheBudget;
            totalPaidBudget += trancheBudget;
          } else {
            projectUnpaidTranches++;
            totalUnpaidTranches++;
            projectUnpaidBudget += trancheBudget;
            totalUnpaidBudget += trancheBudget;
          }

          // Calculer les dépenses réelles de la tranche
          if (tranche.expenses && tranche.expenses.length > 0) {
            const trancheActual = tranche.expenses.reduce((sum, expense) => sum + (expense.amount || 0), 0);
            projectActual += trancheActual;
          }
        });
      }

      const projectResult = projectBudget - projectActual;
      const projectPercentage = projectBudget > 0 ? ((projectActual - projectBudget) / projectBudget * 100) : 0;

      projectStats.push({
        ...project,
        projectBudget,
        projectActual,
        projectResult,
        projectPercentage,
        projectPaidTranches,
        projectUnpaidTranches,
        projectPaidBudget,
        projectUnpaidBudget,
        totalTranches: projectPaidTranches + projectUnpaidTranches
      });

      totalBudget += projectBudget;
      totalActual += projectActual;
    });

    const totalResult = totalBudget - totalActual;
    const resultPercentage = totalBudget > 0 ? ((totalActual - totalBudget) / totalBudget * 100) : 0;

    setGlobalStats({
      totalProjects: projects.length,
      totalBudget,
      totalActual,
      totalPaidTranches,
      totalUnpaidTranches,
      totalPaidBudget,
      totalUnpaidBudget,
      totalResult,
      resultPercentage
    });

    setProjectsWithStats(projectStats);
  };

  return (
    <div className="space-y-6">
      {/* En-tête du tableau de bord global */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">📊 Tableau de Bord Global</h2>
        
        {/* Métriques principales */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white p-4 rounded-lg">
            <p className="text-sm opacity-90">Total Projets</p>
            <p className="text-2xl font-bold">{globalStats.totalProjects}</p>
          </div>
          
          <div className="bg-gradient-to-r from-green-500 to-green-600 text-white p-4 rounded-lg">
            <p className="text-sm opacity-90">Tranches Payées</p>
            <p className="text-2xl font-bold">{globalStats.totalPaidTranches}</p>
            <p className="text-sm opacity-90 mt-1">{formatAmount(globalStats.totalPaidBudget)} Ar</p>
          </div>
          
          <div className="bg-gradient-to-r from-red-500 to-red-600 text-white p-4 rounded-lg">
            <p className="text-sm opacity-90">Tranches Non Payées</p>
            <p className="text-2xl font-bold">{globalStats.totalUnpaidTranches}</p>
            <p className="text-sm opacity-90 mt-1">{formatAmount(globalStats.totalUnpaidBudget)} Ar</p>
          </div>
          
          <div className={`bg-gradient-to-r ${globalStats.totalResult >= 0 ? 'from-green-500 to-green-600' : 'from-red-500 to-red-600'} text-white p-4 rounded-lg`}>
            <p className="text-sm opacity-90">Résultat Global</p>
            <p className="text-2xl font-bold">{formatAmount(globalStats.totalResult)} Ar</p>
          </div>
        </div>

        {/* Résumé financier détaillé */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <div className="bg-gray-50 p-4 rounded-lg">
            <p className="text-sm text-gray-600">Budget Total</p>
            <p className="text-xl font-semibold text-gray-900">{formatAmount(globalStats.totalBudget)} Ar</p>
          </div>
          
          <div className="bg-green-50 p-4 rounded-lg border-l-4 border-green-500">
            <p className="text-sm text-green-600">Budget Tranches Payées</p>
            <p className="text-xl font-semibold text-green-900">{formatAmount(globalStats.totalPaidBudget)} Ar</p>
          </div>
          
          <div className="bg-red-50 p-4 rounded-lg border-l-4 border-red-500">
            <p className="text-sm text-red-600">Budget Tranches Non Payées</p>
            <p className="text-xl font-semibold text-red-900">{formatAmount(globalStats.totalUnpaidBudget)} Ar</p>
          </div>
          
          <div className="bg-gray-50 p-4 rounded-lg">
            <p className="text-sm text-gray-600">Dépenses Réelles</p>
            <p className="text-xl font-semibold text-gray-900">{formatAmount(globalStats.totalActual)} Ar</p>
          </div>
          
          <div className="bg-gray-50 p-4 rounded-lg">
            <p className="text-sm text-gray-600">Écart Global</p>
            <p className={`text-xl font-semibold ${globalStats.resultPercentage <= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {globalStats.resultPercentage >= 0 ? '+' : ''}{globalStats.resultPercentage.toFixed(1)}%
            </p>
          </div>
        </div>
      </div>

      {/* Comparaison par tranche */}
      <div className="bg-white rounded-lg shadow-sm">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Comparaison des Matériaux par Tranche</h3>
          <p className="text-sm text-gray-500 mt-1">Tranches payées uniquement</p>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Projet / Tranche
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Matériaux Prévisionnels
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Montant Prévisionnel
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Montant Réel
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Écart
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Statut
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {projectsWithStats.map((project) => (
                <React.Fragment key={`comparison-${project.id}`}>
                  {project.tranches && project.tranches.filter(tranche => tranche.is_paid).map((tranche) => {
                    const materialCount = tranche.materials ? tranche.materials.length : 0;
                    const plannedTotal = tranche.materials ? tranche.materials.reduce((sum, mat) => sum + (mat.amount_planned || 0), 0) : 0;
                    const actualTotal = tranche.materials ? tranche.materials.reduce((sum, mat) => sum + (mat.amount_actual || 0), 0) : 0;
                    const difference = actualTotal - plannedTotal;
                    const percentage = plannedTotal > 0 ? ((actualTotal - plannedTotal) / plannedTotal * 100) : 0;
                    
                    return (
                      <tr key={`tranche-${tranche.id}`} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-gray-900">{project.name}</div>
                            <div className="text-sm text-gray-500">{tranche.name}</div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {materialCount} matériaux
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatAmount(plannedTotal)} Ar
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatAmount(actualTotal)} Ar
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <div className="flex flex-col">
                            <span className={`font-medium ${difference >= 0 ? 'text-red-600' : 'text-green-600'}`}>
                              {difference >= 0 ? '+' : ''}{formatAmount(difference)} Ar
                            </span>
                            <span className={`text-xs ${percentage >= 0 ? 'text-red-600' : 'text-green-600'}`}>
                              {percentage >= 0 ? '+' : ''}{percentage.toFixed(1)}%
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            Payée
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Tableau détaillé des projets */}
      <div className="bg-white rounded-lg shadow-sm">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Résumé par Projet</h3>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Projet
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Budget
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Dépenses
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Résultat
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Écart %
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tranches
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Statut
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {projectsWithStats.map((project) => (
                <tr key={project.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{project.name}</div>
                      <div className="text-sm text-gray-500">{project.client}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatAmount(project.projectBudget)} Ar
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatAmount(project.projectActual)} Ar
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <span className={`font-medium ${project.projectResult >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {project.projectResult >= 0 ? '+' : ''}{formatAmount(project.projectResult)} Ar
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <span className={`font-medium ${project.projectPercentage <= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {project.projectPercentage >= 0 ? '+' : ''}{project.projectPercentage.toFixed(1)}%
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <div className="flex flex-col space-y-1">
                      <div className="flex items-center space-x-2">
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          {project.projectPaidTranches} payées
                        </span>
                        <span className="text-xs text-gray-500">
                          {formatAmount(project.projectPaidBudget)} Ar
                        </span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          {project.projectUnpaidTranches} non payées
                        </span>
                        <span className="text-xs text-gray-500">
                          {formatAmount(project.projectUnpaidBudget)} Ar
                        </span>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                      project.projectUnpaidTranches === 0 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {project.projectUnpaidTranches === 0 ? 'Terminé' : 'En cours'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button
                      onClick={() => onProjectSelect(project)}
                      className="text-blue-600 hover:text-blue-900"
                    >
                      Voir détails
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

function App() {
  const [currentUser, setCurrentUser] = useState(null);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [showProjectForm, setShowProjectForm] = useState(false);
  const [showGlobalDashboard, setShowGlobalDashboard] = useState(false);
  const [loading, setLoading] = useState(true);

  // Vérifier l'authentification au chargement
  useEffect(() => {
    const storedUser = localStorage.getItem("currentUser");
    if (storedUser) {
      try {
        setCurrentUser(JSON.parse(storedUser));
      } catch (error) {
        localStorage.removeItem("currentUser");
      }
    }
    setIsCheckingAuth(false);
  }, []);

  useEffect(() => {
    if (currentUser) {
      fetchProjects();
    }
  }, [currentUser]);

  // Fonction de connexion
  const handleLogin = (user) => {
    setCurrentUser({
      username: user.username,
      name: user.name,
      role: user.role,
      loginTime: new Date().toISOString()
    });
  };

  // Fonctions de vérification des permissions
  const canAccessTab = (tabName) => {
    if (!currentUser) return false;
    
    const role = currentUser.role;
    
    switch (role) {
      case "Admin":
        return true; // Admin peut tout voir
        
      case "Comptable":
        // Comptable peut voir tout sauf certains éléments sensibles
        return ['overview', 'tranches', 'expenses', 'materials', 'comparison', 'salaires', 'orders'].includes(tabName);
        
      case "Logistique":
        // Logistique ne peut voir que les matériaux et commandes
        return ['materials', 'orders'].includes(tabName);
        
      default:
        return false;
    }
  };

  const canAccessGlobalDashboard = () => {
    return currentUser && ['Admin', 'Comptable'].includes(currentUser.role);
  };

  const canCreateProjects = () => {
    return currentUser && ['Admin'].includes(currentUser.role);
  };

  const canManageOrders = () => {
    return currentUser && ['Admin', 'Logistique', 'Comptable'].includes(currentUser.role);
  };

  // Fonction de déconnexion
  const handleLogout = () => {
    localStorage.removeItem("currentUser");
    setCurrentUser(null);
    setProjects([]);
    setSelectedProject(null);
    setShowProjectForm(false);
    setShowGlobalDashboard(false);
    setLoading(true);
  };

  // Afficher l'écran de connexion si pas connecté
  if (isCheckingAuth) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!currentUser) {
    return <LoginForm onLogin={handleLogin} />;
  }

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`);
      setProjects(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching projects:', error);
      setLoading(false);
    }
  };

  const handleCreateProject = async (projectData) => {
    try {
      const response = await axios.post(`${API}/projects`, projectData);
      setProjects([...projects, response.data]);
      setShowProjectForm(false);
    } catch (error) {
      console.error('Error creating project:', error);
    }
  };

  const handleUpdateProject = () => {
    fetchProjects();
  };

  const handleDeleteProject = () => {
    setSelectedProject(null);
    fetchProjects();
  };

  const handleProjectSelect = (project) => {
    setSelectedProject(project);
    setShowGlobalDashboard(false);
  };

  const handleBackToProjects = () => {
    setSelectedProject(null);
    setShowGlobalDashboard(false);
    fetchProjects();
  };

  const handleShowGlobalDashboard = () => {
    setShowGlobalDashboard(true);
    setSelectedProject(null);
  };

  if (selectedProject) {
    return (
      <ProjectDetail
        project={selectedProject}
        onBack={handleBackToProjects}
        onUpdate={handleUpdateProject}
        onDelete={handleDeleteProject}
        currentUser={currentUser}
        canAccessTab={canAccessTab}
      />
    );
  }

  if (showGlobalDashboard) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="bg-white shadow-sm border-b">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <div className="flex items-center">
                <button
                  onClick={handleBackToProjects}
                  className="mr-4 p-2 text-gray-500 hover:text-gray-700"
                >
                  ← Retour aux projets
                </button>
                <h1 className="text-xl font-semibold text-gray-900">Tableau de Bord Global</h1>
              </div>
              <div className="flex items-center space-x-4">
                <span className="text-sm text-gray-600">Bienvenue, {currentUser.name}</span>
                <button
                  onClick={handleLogout}
                  className="inline-flex items-center px-3 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 transition-all text-sm"
                >
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                  Déconnexion
                </button>
                <ArchyStoreLogo />
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <GlobalDashboard projects={projects} onProjectSelect={handleProjectSelect} />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-red-50 to-orange-100">
      <div className="bg-white shadow-lg border-b-2 border-orange-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <ArchyStoreLogo />
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-600">Bienvenue, {currentUser.name} ({currentUser.role})</span>
              <span className="text-sm text-gray-600 font-semibold">Gestion Budgétaire</span>
              {canAccessGlobalDashboard() && (
                <button
                  onClick={handleShowGlobalDashboard}
                  className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all transform hover:scale-105 shadow-lg"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                  <span className="font-semibold">Tableau de Bord</span>
                </button>
              )}
              {canCreateProjects() && (
                <button
                  onClick={() => setShowProjectForm(true)}
                  className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-lg hover:from-orange-600 hover:to-red-600 transition-all transform hover:scale-105 shadow-lg"
                >
                  <PlusIcon />
                  <span className="ml-2 font-semibold">Nouveau Chantier</span>
                </button>
              )}
              <button
                onClick={handleLogout}
                className="inline-flex items-center px-3 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 transition-all text-sm"
              >
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                Déconnexion
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
          </div>
        ) : projects.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-24 h-24 bg-gradient-to-br from-orange-500 to-red-500 rounded-full mx-auto mb-6 flex items-center justify-center shadow-xl">
              <svg className="w-12 h-12 text-white" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
              </svg>
            </div>
            <h3 className="text-2xl font-bold text-gray-800 mb-2">Bienvenue sur ARCHYSTORE</h3>
            <p className="text-gray-600 mb-8">Commencez par créer votre premier chantier</p>
            <button
              onClick={() => setShowProjectForm(true)}
              className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-lg hover:from-orange-600 hover:to-red-600 transition-all transform hover:scale-105 shadow-lg"
            >
              <PlusIcon />
              <span className="ml-2 font-semibold">Créer votre premier chantier</span>
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {projects.map((project) => (
              <div
                key={project.id}
                className="bg-white rounded-xl shadow-lg border-2 border-orange-200 hover:shadow-xl transition-all duration-300 cursor-pointer transform hover:scale-105 hover:border-orange-300"
                onClick={() => handleProjectSelect(project)}
              >
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-bold text-gray-900">{project.name}</h3>
                    <div className="w-8 h-8 bg-gradient-to-br from-orange-500 to-red-500 rounded-lg flex items-center justify-center">
                      <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 2L2 7l10 5 10-5-10-5z"/>
                      </svg>
                    </div>
                  </div>
                  <div className="space-y-2 text-sm text-gray-600 mb-4">
                    <div className="flex items-center">
                      <svg className="w-4 h-4 mr-2 text-orange-500" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                      </svg>
                      {project.location}
                    </div>
                    <div className="flex items-center">
                      <svg className="w-4 h-4 mr-2 text-orange-500" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                      </svg>
                      {project.client}
                    </div>
                    <div className="flex items-center">
                      <svg className="w-4 h-4 mr-2 text-orange-500" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M9 11H7v6h2v-6zm4 0h-2v6h2v-6zm4 0h-2v6h2v-6zm2-7h-3V2h-2v2H8V2H6v2H3c-1.11 0-2 .89-2 2v14c0 1.11.89 2 2 2h14c1.11 0 2-.89 2-2V6c0-1.11-.89-2-2-2zm0 16H3V8h14v12z"/>
                      </svg>
                      Début: {project.start_date ? formatDateEuropean(project.start_date) : 'Non spécifiée'}
                    </div>
                  </div>
                  <div className="bg-gradient-to-r from-orange-500 to-red-500 text-white p-4 rounded-lg">
                    <p className="text-sm opacity-90">Budget total</p>
                    <p className="text-2xl font-bold">
                      {formatAmount(project.total_budget)} Ar
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showProjectForm && (
        <ProjectForm
          onSubmit={handleCreateProject}
          onCancel={() => setShowProjectForm(false)}
        />
      )}
    </div>
  );
}

// Composant OrderForm
const OrderForm = ({ material, onSubmit, onCancel, maxQuantity }) => {
  const [formData, setFormData] = useState({
    quantity_ordered: material ? material.quantity_remaining : '',
    unit_price: material ? material.unit_price : '',
    supplier: '',
    order_date: new Date().toISOString().split('T')[0],
    expected_delivery_date: '',
    payment_date: '',
    notes: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const orderData = {
      material_designation: material ? material.designation : '',
      quantity_ordered: parseFloat(formData.quantity_ordered),
      unit: material ? material.unit : '',
      unit_price: parseFloat(formData.unit_price),
      supplier: formData.supplier,
      order_date: formData.order_date,
      expected_delivery_date: formData.expected_delivery_date,
      payment_date: formData.payment_date || null,
      notes: formData.notes || null
    };

    onSubmit(orderData);
  };

  const handleChange = (field) => (e) => {
    setFormData({ ...formData, [field]: e.target.value });
  };

  const calculatedTotal = formData.quantity_ordered && formData.unit_price 
    ? parseFloat(formData.quantity_ordered) * parseFloat(formData.unit_price)
    : 0;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-bold mb-4">
          {material ? `Commander - ${material.designation}` : 'Nouvelle Commande'}
        </h2>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          {!material && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Désignation du matériau
              </label>
              <input
                type="text"
                value={formData.material_designation || ''}
                onChange={handleChange('material_designation')}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
          )}
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Quantité à commander
              {material && (
                <span className="text-sm text-gray-500 ml-1">
                  (Max: {material.quantity_remaining} {material.unit})
                </span>
              )}
            </label>
            <input
              type="number"
              step="0.01"
              value={formData.quantity_ordered}
              onChange={handleChange('quantity_ordered')}
              max={material ? material.quantity_remaining : undefined}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Prix unitaire (Ar)
            </label>
            <input
              type="number"
              step="0.01"
              value={formData.unit_price}
              onChange={handleChange('unit_price')}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Fournisseur
            </label>
            <input
              type="text"
              value={formData.supplier}
              onChange={handleChange('supplier')}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
              placeholder="Nom du fournisseur"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Date de commande
            </label>
            <input
              type="date"
              value={formData.order_date}
              onChange={handleChange('order_date')}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Date de livraison prévue
            </label>
            <input
              type="date"
              value={formData.expected_delivery_date}
              onChange={handleChange('expected_delivery_date')}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Date de paiement (optionnel)
            </label>
            <input
              type="date"
              value={formData.payment_date}
              onChange={handleChange('payment_date')}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes (optionnel)
            </label>
            <textarea
              value={formData.notes}
              onChange={handleChange('notes')}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
              rows="3"
              placeholder="Notes supplémentaires..."
            />
          </div>

          <div className="bg-blue-50 p-3 rounded-md">
            <div className="text-sm text-blue-800">
              <strong>Total estimé: {formatAmount(calculatedTotal)} Ar</strong>
            </div>
          </div>

          <div className="flex justify-end space-x-2 pt-4">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Annuler
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
            >
              Créer la commande
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default App;